import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, Routes, Route, useLocation, useSearchParams, useMatch } from 'react-router-dom';
import { Phone, Users, Clock, Calendar, Plus, Search, Home, Settings, HelpCircle, LogOut, CalendarCheck, UserPlus, Trash2, Mail, X, CheckCircle, CreditCard, DollarSign, Dog, ChevronDown, ChevronRight, FileText, Pill, Syringe, Activity, Receipt, File, MessageSquare, FlaskConical, Layout, Edit, Bell, History, Megaphone, Eye, Upload, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';
import { APP_NAME } from '../config/branding';
import { getRoleDisplay } from '../utils/roleUtils';
import { formatPhoneNumber } from '../utils/phoneUtils';
import PetOwnerDetailModal from '../components/pet-owners/PetOwnerDetailModal';
import PetOwnerDetail from '../components/pet-owners/PetOwnerDetail';
import PetOwnerEditModal from '../components/pet-owners/PetOwnerEditModal';
import PetOwnerForm from '../components/pet-owners/PetOwnerForm';
import PetOwnerCreateModal from '../components/pet-owners/PetOwnerCreateModal';
import CsvImportModal from '../components/pet-owners/CsvImportModal';
import PetDetail from '../components/pets/PetDetail';
import PetEditModal from '../components/pets/PetEditModal';
import RemindersDropdown from '../components/pet-owners/RemindersDropdown';
import PetsDropdown from '../components/pet-owners/PetsDropdown';
import CustomerStatusDropdown from '../components/pet-owners/CustomerStatusDropdown';
import { CustomerStatus, PetOwnerCrmData } from '../types/crm';
import { Pet } from '../types/pet';
import AppointmentCreateModal from '../components/appointments/AppointmentCreateModal';
import AppointmentViewModal from '../components/appointments/AppointmentViewModal';
import AppointmentEditModal from '../components/appointments/AppointmentEditModal';
import AppointmentForm from '../components/appointments/AppointmentForm';
import ConfirmDialog from '../components/common/ConfirmDialog';
import CallHistorySection from '../components/calls/CallHistorySection';
import AppointmentCalendar from '../components/appointments/AppointmentCalendar';
import AppointmentSwimLanesView from '../components/appointments/AppointmentSwimLanesView';
import PatientFlowView from '../components/appointments/PatientFlowView';
import WorkScheduleCalendar from '../components/work-schedule/WorkScheduleCalendar';
import WorkScheduleSwimLanesView from '../components/work-schedule/WorkScheduleSwimLanesView';
import Breadcrumb from '../components/common/Breadcrumb';
import { UserAvatar } from '../components/common/UserAvatar';
import ChatButton from '../components/chat/ChatButton';
import { useChatDock } from '../contexts/ChatDockContext';
import NotificationsBell from '../components/notifications/NotificationsBell';
import NotificationsPage from '../components/notifications/NotificationsPage';
import * as notificationsApi from '../utils/notificationsApi';
import WorkScheduleCreateModal from '../components/work-schedule/WorkScheduleCreateModal';
import WorkScheduleEditModal from '../components/work-schedule/WorkScheduleEditModal';
import ViewEventModal from '../components/work-schedule/ViewEventModal';
import { RecurringEventActionModal } from '../components/work-schedule/RecurringEventActionModal';
import ConfirmDeleteModal from '../components/common/ConfirmDeleteModal';
import {
  fetchCachedTeamMembers,
  fetchAllTeamMembers as fetchAllTeamMembersCached,
  invalidateTeamMembersCache,
  type TeamMember
} from '../utils/teamMembersCache';
import BillingAdminDashboard from '../components/admin/BillingAdminDashboard';
import MedicalRecordHistory from '../components/medical-records/MedicalRecordHistory';
import { ImmunizationsList } from '../components/immunizations/ImmunizationsList';
import PetLabs from '../components/pets/PetLabs';
import PetMedications from '../components/pets/PetMedications';
import PetBilling from '../components/pets/PetBilling';
import PetForms from '../components/pets/PetForms';
import { ImmunizationScheduleView } from '../components/immunizations/ImmunizationScheduleView';
import { OverdueAlertBanner } from '../components/immunizations/OverdueAlertBanner';
import { Immunization } from '../types/immunization';
import { ImmunizationFormModal } from '../components/immunizations/ImmunizationFormModal';
import MedicalRecordForm from '../components/medical-records/MedicalRecordForm';
import MedicalRecordDetail from '../components/medical-records/MedicalRecordDetail';
import PetForm from '../components/pets/PetForm';
import ReminderEditPage from '../components/pets/ReminderEditPage';
import PetAuditLog from '../components/pets/PetAuditLog';
import BroadcastPage, { BROADCAST_TABS, NotificationsHandle, BroadcastTab, getBroadcastViewInfo } from '../pages/Notifications';
import SetAndForgetPage, { SetAndForgetHandle, getSetAndForgetBreadcrumbInfo } from '../pages/SetAndForget';
import { TemplatesManager, TemplateEditView, CreateTemplatePage, ListsManager } from '../components/notifications';
import NotificationAuditLog from '../components/settings/NotificationAuditLog';
import PetOwnerAuditLog from '../components/pet-owners/PetOwnerAuditLog';
// Chat UI is now mounted globally via ChatDockProvider (App.tsx)

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const { openChat, unreadCount: chatUnreadCount, isOpen: isChatOpen } = useChatDock();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [clientsPage, setClientsPage] = useState(1);
  const [clientsPageSize] = useState(25);
  const [clientsTotalPages, setClientsTotalPages] = useState(1);
  const [clientsTotal, setClientsTotal] = useState(0);
  // RESTful filters from URL params
  const statusFilter = searchParams.get('status') || null;
  const appointmentsTodayFilter = searchParams.get('today') === 'true';
  const needsAppointmentFilter = searchParams.get('needs_appt') === 'true';
  
  const setStatusFilter = (status: string | null) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (status) {
        newParams.set('status', status);
      } else {
        newParams.delete('status');
      }
      newParams.delete('today'); // Clear today filter when setting status
      newParams.delete('needs_appt'); // Clear needs_appt filter when setting status
      newParams.delete('page'); // Reset to page 1
      return newParams;
    });
    setClientsPage(1);
  };
  
  const setAppointmentsTodayFilter = (enabled: boolean) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (enabled) {
        newParams.set('today', 'true');
        newParams.delete('status'); // Clear status filter when filtering by today
        newParams.delete('needs_appt'); // Clear needs_appt filter
      } else {
        newParams.delete('today');
      }
      newParams.delete('page'); // Reset to page 1
      return newParams;
    });
    setClientsPage(1);
  };
  
  const setNeedsAppointmentFilter = (enabled: boolean) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (enabled) {
        newParams.set('needs_appt', 'true');
        newParams.delete('status'); // Clear status filter
        newParams.delete('today'); // Clear today filter
      } else {
        newParams.delete('needs_appt');
      }
      newParams.delete('page'); // Reset to page 1
      return newParams;
    });
    setClientsPage(1);
  };
  const [clientsStats, setClientsStats] = useState<{ total: number; prospect: number; open: number; cold: number; lost: number; appointments_today: number; needs_appointment: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // LocalStorage keys for persistent user preferences
  const STORAGE_KEYS = {
    VET_SELECTION: 'helppet_vet_selection', // Single key for all views (appointments, schedule)
    VET_SELECTION_INITIALIZED: 'helppet_vet_selection_initialized', // Flag to know if user has made a selection
    CALENDAR_VIEW: 'helppet_calendar_view',
    APPOINTMENT_VIEW_MODE: 'helppet_appointment_view_mode',
    SCHEDULE_VIEW_MODE: 'helppet_schedule_view_mode',
    TEAM_MEMBERS_CACHE: 'helppet_team_members_cache', // Cache for practice members
  };

  const TEAM_MEMBERS_TTL = 15 * 60 * 1000; // 15 minutes - team rarely changes

  // Helper functions for localStorage with error handling
  const getStoredVetSelection = (key: string): Set<string> => {
    try {
      const stored = localStorage.getItem(key);
      if (stored) {
        const parsed = JSON.parse(stored);
        return new Set(Array.isArray(parsed) ? parsed : []);
      }
    } catch (error) {
      console.warn(`Failed to parse stored vet selection from ${key}:`, error);
    }
    return new Set();
  };

  const setStoredVetSelection = (key: string, vetIds: Set<string>) => {
    try {
      localStorage.setItem(key, JSON.stringify(Array.from(vetIds)));
      // Mark as initialized
      localStorage.setItem(STORAGE_KEYS.VET_SELECTION_INITIALIZED, 'true');
    } catch (error) {
      console.warn(`Failed to store vet selection to ${key}:`, error);
    }
  };

  const isVetSelectionInitialized = (): boolean => {
    return localStorage.getItem(STORAGE_KEYS.VET_SELECTION_INITIALIZED) === 'true';
  };

  // Team members cache helpers
  const getStoredTeamMembers = (): { members: TeamMember[], timestamp: number } | null => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.TEAM_MEMBERS_CACHE);
      if (stored) {
        const parsed = JSON.parse(stored);
        const now = Date.now();
        // Check if cache is still fresh (within TTL)
        if (parsed.timestamp && (now - parsed.timestamp) < TEAM_MEMBERS_TTL) {
          return parsed;
        }
      }
    } catch (error) {
      console.warn('Failed to parse stored team members:', error);
    }
    return null;
  };

  const setStoredTeamMembers = (members: TeamMember[]) => {
    try {
      const cacheData = {
        members,
        timestamp: Date.now()
      };
      localStorage.setItem(STORAGE_KEYS.TEAM_MEMBERS_CACHE, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Failed to store team members cache:', error);
    }
  };

  // SHARED vet selection for ALL views (appointments, schedule, etc)
  // Restored from localStorage so it persists across sessions
  const [selectedVetIds, setSelectedVetIds] = useState<Set<string>>(() => {
    const stored = getStoredVetSelection(STORAGE_KEYS.VET_SELECTION);
    return stored;
  });

  // Alias for compatibility (both views use the same selection)
  const selectedScheduleVetIds = selectedVetIds;

  // const petMatch = useMatch('/dashboard/pets/:uuid');
  const petUuidMatch = location.pathname.match(/\/dashboard\/pets\/([^/]+)/);
  const petUuid = petUuidMatch ? petUuidMatch[1] : null;

  const medicalRecordDetailMatch = location.pathname.match(/\/dashboard\/pets\/([a-f0-9-]+)\/medical-records\/([a-f0-9-]+)$/);
  const medicalRecordEditMatch = location.pathname.match(/\/dashboard\/pets\/([a-f0-9-]+)\/medical-records\/([a-f0-9-]+)\/edit/);
  const recordId = medicalRecordDetailMatch ? medicalRecordDetailMatch[2] : (medicalRecordEditMatch ? medicalRecordEditMatch[2] : undefined);
  const reminderEditMatch = location.pathname.match(/\/dashboard\/pets\/([a-f0-9-]+)\/reminders\/([a-f0-9-]+)\/edit/);
  const reminderId = reminderEditMatch ? reminderEditMatch[2] : undefined;
  const reminderPetId = reminderEditMatch ? reminderEditMatch[1] : undefined;

  console.log('Dashboard Render:', {
    path: location.pathname,
    petUuid,
    recordId
  });

  // Derived state for active section
  const getActiveSectionFromPath = (): 'calls' | 'schedule' | 'pets' | 'appointments' | 'flow' | 'notifications' | 'admin-billing' | 'broadcast' | 'set-and-forget' | 'templates' | 'audit-log' | 'audience' | 'client-audit' => {
    const path = location.pathname;
    if (path.includes('/client-audit')) return 'client-audit';
    if (path.includes('/pet_owners')) return 'pets'; // Legacy support
    if (path.includes('/pets')) return 'pets';
    if (path.includes('/audience')) return 'audience';
    if (path.includes('/appointments')) return 'appointments';
    if (path.includes('/flow')) return 'flow';
    if (path.includes('/calls')) return 'calls';
    if (path.includes('/schedule')) return 'schedule';
    if (path.includes('/set-and-forget')) return 'set-and-forget';
    if (path.includes('/templates')) return 'templates';
    if (path.includes('/audit-log')) return 'audit-log';
    if (path.includes('/broadcast')) return 'broadcast';
    if (path.includes('/notifications')) return 'notifications';
    if (path.includes('/admin/billing')) return 'admin-billing';
    return 'appointments'; // default
  };

  const activeSection = getActiveSectionFromPath();

  // Real data from API
  const [calls, setCalls] = useState<any[]>([]);
  const [schedule, setSchedule] = useState<any[]>([]);
  const [owners, setOwners] = useState<any[]>([]);
  const [ownersCrmData, setOwnersCrmData] = useState<Record<string, PetOwnerCrmData>>({}); // CRM data keyed by owner UUID
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]); // ONE source of truth for ALL team members
  const [pendingInvites, setPendingInvites] = useState<any[]>([]);
  const [appointments, setAppointments] = useState<any[]>([]);

  // Notifications badge state (persisted via backend; updated in realtime via websocket event)
  const [notificationsUnreadCount, setNotificationsUnreadCount] = useState(0);

  // Modal state
  const [selectedOwnerId, setSelectedOwnerId] = useState<string | null>(null);
  const [selectedOwnerName, setSelectedOwnerName] = useState<string | null>(null);
  const [isOwnerModalOpen, setIsOwnerModalOpen] = useState(false);
  const [isOwnerEditModalOpen, setIsOwnerEditModalOpen] = useState(false);
  const [isCreateOwnerOpen, setIsCreateOwnerOpen] = useState(false);
  const [isCreateAppointmentOpen, setIsCreateAppointmentOpen] = useState(false);
  const [isCsvImportOpen, setIsCsvImportOpen] = useState(false);
  const [isRefreshingCrm, setIsRefreshingCrm] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<{ start: Date; end: Date; vetUserId?: string } | null>(null);
  const [selectedPetId, setSelectedPetId] = useState<string | null>(null);
  const [selectedPetName, setSelectedPetName] = useState<string | null>(null);
  const [selectedAudienceName, setSelectedAudienceName] = useState<string | null>(null);
  const [selectedTemplateName, setSelectedTemplateName] = useState<string | null>(null);

  // Pre-selected pet/owner for appointment creation (from Book Appointment button)
  const [appointmentPetId, setAppointmentPetId] = useState<string | null>(null);
  const [appointmentOwnerId, setAppointmentOwnerId] = useState<string | null>(null);

  const [isPetEditModalOpen, setIsPetEditModalOpen] = useState(false);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<string | null>(null);

  const [selectedAppointment, setSelectedAppointment] = useState<any | null>(null);
  const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState(false);
  const [isAppointmentEditModalOpen, setIsAppointmentEditModalOpen] = useState(false);
  const [showDeleteAppointmentConfirm, setShowDeleteAppointmentConfirm] = useState(false);
  const [ownerMenuOpen, setOwnerMenuOpen] = useState<string | null>(null);
  const [latestMedicalRecordId, setLatestMedicalRecordId] = useState<string | null>(null);
  
  // Campaign preview modal state (for header button)
  const [showCampaignPreviewModal, setShowCampaignPreviewModal] = useState(false);

  // Recent Pets State
  const [recentPets, setRecentPets] = useState<{ id: string; name: string }[]>([]);
  const [isPetSubmenuOpen, setIsPetSubmenuOpen] = useState(true); // For the current pet's sub-menu

  // Chat UI moved to global ChatDockProvider
  const [isPetsSectionOpen, setIsPetsSectionOpen] = useState(true); // For the list of recent pets

  // Immunization state
  const [isImmunizationModalOpen, setIsImmunizationModalOpen] = useState(false);
  const [selectedImmunization, setSelectedImmunization] = useState<Immunization | undefined>(undefined);
  const [immunizationRefreshTrigger, setImmunizationRefreshTrigger] = useState(0);

  // Work Schedule Modal state
  const [isCreateScheduleOpen, setIsCreateScheduleOpen] = useState(false);
  const [selectedScheduleEvent, setSelectedScheduleEvent] = useState<any>(null);
  const [isEditScheduleOpen, setIsEditScheduleOpen] = useState(false);
  const [selectedScheduleSlot, setSelectedScheduleSlot] = useState<{ start: Date; end: Date; vetId?: string } | null>(null);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);
  
  // Broadcast page ref for triggering create actions from header
  const broadcastRef = useRef<NotificationsHandle>(null);
  const setAndForgetRef = useRef<SetAndForgetHandle>(null);
  const templatesManagerRef = useRef<{ openCreate: () => void }>(null);
  const audienceManagerRef = useRef<{ openCreate: () => void; openEdit: () => void; getSelectedListName: () => string | null }>(null);

  // Team invite state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [isInviting, setIsInviting] = useState(false);

  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [revokeInviteId, setRevokeInviteId] = useState<string | null>(null);
  const [removeMemberId, setRemoveMemberId] = useState<string | null>(null);

  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Collapsible sidebar sections - Communications collapsed by default
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(() => new Set(['COMMUNICATIONS']));

  const toggleSectionCollapse = (section: string) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  // Cache for schedule data (5-minute TTL)
  const scheduleCache = useRef<{
    data: any[];
    timestamp: number;
    key: string;
  } | null>(null);
  const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  // Debounce timer for calendar navigation
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const DEBOUNCE_DELAY = 50; // 50ms - prevents double-clicks but feels immediate

  // Calendar state - initialize from URL params
  const getInitialCalendarDate = () => {
    const dateParam = searchParams.get('date');
    if (dateParam) {
      // Parse YYYY-MM-DD as LOCAL date, not UTC
      // new Date('2025-11-04') creates UTC midnight, which is wrong
      // We need to create a local date at midnight
      const [year, month, day] = dateParam.split('-').map(Number);
      const parsed = new Date(year, month - 1, day); // Local date at midnight
      if (!isNaN(parsed.getTime())) {
        return parsed;
      }
    }
    return new Date();
  };

  const getInitialCalendarView = () => {
    const viewParam = searchParams.get('view');
    if (viewParam && ['month', 'week', 'day', 'agenda'].includes(viewParam)) {
      return viewParam;
    }
    return 'week';
  };

  const getInitialViewMode = () => {
    const modeParam = searchParams.get('mode');
    if (modeParam === 'calendar') {
      return 'calendar';
    }
    return 'swimlanes'; // Default to swim lanes view
  };

  const [calendarDate, setCalendarDate] = useState(getInitialCalendarDate());
  const [calendarView, setCalendarView] = useState<any>(getInitialCalendarView());
  const [appointmentViewMode, setAppointmentViewMode] = useState<'calendar' | 'swimlanes'>(getInitialViewMode());
  const [scheduleViewMode, setScheduleViewMode] = useState<'calendar' | 'swimlanes'>(getInitialViewMode());

  // Ref to always have the current calendarDate (avoids stale closure in debounced callbacks)
  const calendarDateRef = useRef<Date>(calendarDate); // Read from URL params

  // Update URL when calendar state changes
  const updateCalendarUrl = (date: Date, view: string, mode: 'calendar' | 'swimlanes') => {
    if (activeSection === 'appointments' || activeSection === 'schedule') {
      const params = new URLSearchParams(searchParams);
      // Use local date components to avoid UTC timezone issues
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      params.set('date', `${year}-${month}-${day}`);
      params.set('view', view);
      params.set('mode', mode);
      setSearchParams(params, { replace: true });
    }
  };

  const handleCalendarDateChange = (newDate: Date) => {
    setCalendarDate(newDate);
    calendarDateRef.current = newDate; // Update ref immediately for debounced callback
    const mode = activeSection === 'schedule' ? scheduleViewMode : appointmentViewMode;
    updateCalendarUrl(newDate, calendarView, mode);

    // Clear existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // IMPORTANT: Capture current section and team state to avoid stale closure
    const currentSection = activeSection;
    const currentTeamMembersCount = teamMembers.length;
    const currentSelectedScheduleVets = new Set(selectedScheduleVetIds);
    const currentSelectedAppointmentVets = new Set(selectedVetIds);

    // Short debounce (50ms) - prevents double-clicks but feels immediate
    debounceTimer.current = setTimeout(() => {
      // Both schedule and appointments use the same team members now
      if (currentSection === 'schedule' && currentTeamMembersCount > 0 && currentSelectedScheduleVets.size > 0) {
        fetchScheduleData();
      } else if (currentSection === 'appointments' && currentTeamMembersCount > 0 && currentSelectedAppointmentVets.size > 0) {
        fetchData();
      }
    }, DEBOUNCE_DELAY);
  };

  const handleCalendarViewChange = (newView: any) => {
    setCalendarView(newView);
    updateCalendarUrl(calendarDate, newView, appointmentViewMode);
  };

  const handleViewModeChange = (newMode: 'calendar' | 'swimlanes') => {
    setAppointmentViewMode(newMode);
    updateCalendarUrl(calendarDate, calendarView, newMode);
  };

  // Check if user is admin
  const isAdmin = user?.role === 'ADMIN';

  const navigationItems = [
    // CLINIC OPERATIONS (top priority - patient flow & scheduling)
    { key: 'flow' as const, label: 'Patient Flow', icon: <Users className="w-4 h-4" />, section: 'CLINIC OPERATIONS', path: '/dashboard/flow' },
    { key: 'appointments' as const, label: 'Appointments', icon: <CalendarCheck className="w-4 h-4" />, section: 'CLINIC OPERATIONS', path: '/dashboard/appointments' },
    { key: 'schedule' as const, label: 'Work Schedule', icon: <Calendar className="w-4 h-4" />, section: 'CLINIC OPERATIONS', path: '/dashboard/schedule' },

    // CRM
    { key: 'pets' as const, label: 'Clients', icon: <Dog className="w-4 h-4" />, section: 'CRM', path: '/dashboard/pets' },
    { key: 'client-audit' as const, label: 'Client Audit', icon: <History className="w-4 h-4" />, section: 'CRM', path: '/dashboard/client-audit' },

    // COMMUNICATIONS
    { key: 'broadcast' as const, label: 'Broadcast', icon: <Megaphone className="w-4 h-4" />, section: 'COMMUNICATIONS', path: '/dashboard/broadcast' },
    { key: 'audience' as const, label: 'Audience Manager', icon: <Users className="w-4 h-4" />, section: 'COMMUNICATIONS', path: '/dashboard/audience' },
    { key: 'set-and-forget' as const, label: 'Set And Forget', icon: <Bell className="w-4 h-4" />, section: 'COMMUNICATIONS', path: '/dashboard/set-and-forget' },
    { key: 'templates' as const, label: 'Templates', icon: <FileText className="w-4 h-4" />, section: 'COMMUNICATIONS', path: '/dashboard/templates' },
    { key: 'audit-log' as const, label: 'Audit Log', icon: <History className="w-4 h-4" />, section: 'COMMUNICATIONS', path: '/dashboard/audit-log' },

    // FRONT DESK
    { key: 'calls' as const, label: 'Call History', icon: <Clock className="w-4 h-4" />, section: 'FRONT DESK', path: '/dashboard/calls' },

    // ADMIN
    ...(isAdmin ? [{ key: 'admin-billing' as const, label: 'Admin: Billing', icon: <DollarSign className="w-4 h-4" />, section: 'ADMIN', path: '/dashboard/admin/billing' }] : []),
  ];

  // Redirect to default tab if on base /dashboard
  useEffect(() => {
    if (location.pathname === '/dashboard' || location.pathname === '/dashboard/') {
      navigate('/dashboard/appointments', { replace: true });
    }
  }, [location.pathname, navigate]);

  // Load persisted unread count
  useEffect(() => {
    if (!user?.practice_id) return;
    (async () => {
      try {
        const resp = await notificationsApi.getUnreadCount();
        setNotificationsUnreadCount(resp.unread_count || 0);
      } catch {
        // ignore
      }
    })();
  }, [user?.practice_id]);

  // Real-time notifications via websocket (useClinicChat dispatches hp_notification_new)
  useEffect(() => {
    const handler = () => {
      setNotificationsUnreadCount((prev) => prev + 1);
    };
    window.addEventListener('hp_notification_new', handler as EventListener);
    return () => window.removeEventListener('hp_notification_new', handler as EventListener);
  }, []);

  // Auto-expand COMMUNICATIONS section when navigating to a communications page
  useEffect(() => {
    if (['broadcast', 'audience', 'set-and-forget', 'templates', 'audit-log'].includes(activeSection)) {
      setCollapsedSections(prev => {
        if (prev.has('COMMUNICATIONS')) {
          const next = new Set(prev);
          next.delete('COMMUNICATIONS');
          return next;
        }
        return prev;
      });
    }
  }, [activeSection]);

  // Deep-link: open appointment modal by query param (?appointment_id=...)
  const lastHandledAppointmentIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (activeSection !== 'appointments') return;
    const apptId = searchParams.get('appointment_id');
    if (!apptId) return;
    if (lastHandledAppointmentIdRef.current === apptId) return;

    lastHandledAppointmentIdRef.current = apptId;
    (async () => {
      try {
        const token = localStorage.getItem('access_token') || localStorage.getItem('token');
        if (!token) return;
        const resp = await fetch(`${API_BASE_URL}/api/v1/appointments/${apptId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!resp.ok) return;
        const appointmentData = await resp.json();
        setSelectedAppointmentId(apptId);
        setSelectedAppointment(appointmentData);
        setIsAppointmentModalOpen(true);
      } catch {
        // ignore
      }
    })();
  }, [activeSection, searchParams, API_BASE_URL]);

  // Deep-link: open appointment EDIT modal by query param (?edit=<appointment_id>)
  // RESTful: URL reflects state - edit param stays in URL while modal is open
  const editAppointmentId = activeSection === 'appointments' ? searchParams.get('edit') : null;
  
  useEffect(() => {
    if (activeSection !== 'appointments') return;
    
    // Sync calendarDate with URL date param when entering appointments section
    const dateParam = searchParams.get('date');
    if (dateParam && editAppointmentId) {
      const [year, month, day] = dateParam.split('-').map(Number);
      const urlDate = new Date(year, month - 1, day);
      if (!isNaN(urlDate.getTime()) && urlDate.getTime() !== calendarDate.getTime()) {
        setCalendarDate(urlDate);
        calendarDateRef.current = urlDate;
      }
    }
  }, [activeSection, searchParams, editAppointmentId]);
  
  // Close edit modal by clearing URL param (RESTful)
  const closeEditModal = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('edit');
    setSearchParams(newParams, { replace: true });
  };

  // Check URL for modal opens
  useEffect(() => {
    const path = location.pathname;

    // Reset all modal states first to ensure clean transitions
    setIsCreateOwnerOpen(false);
    setIsOwnerModalOpen(false);
    setIsOwnerEditModalOpen(false);
    setIsAppointmentModalOpen(false);
    setSelectedOwnerId(null);
    setSelectedPetId(null);
    setSelectedAppointmentId(null);

    // /dashboard/pet_owners/new
    if (path.includes('/pet_owners/new')) {
      setIsCreateOwnerOpen(true);
      return;
    }

    // /dashboard/pet_owners/{uuid}/pets/{petUuid}
    // /dashboard/pet_owners/{uuid}/pets/{petUuid}
    const petOwnerPetMatch = path.match(/\/dashboard\/pet_owners\/([a-f0-9-]+)\/pets\/([a-f0-9-]+)/);
    if (petOwnerPetMatch) {
      const [, ownerUuid, petUuid] = petOwnerPetMatch;
      // Redirect to new pet detail page
      navigate(`/pets/${petUuid}`);
      return;
    }

    // /dashboard/pet_owners/{uuid} - Show owner detail in dashboard
    const petOwnerMatch = path.match(/\/dashboard\/pet_owners\/([a-f0-9-]+)(?:\/edit)?/);
    if (petOwnerMatch) {
      const [, ownerUuid] = petOwnerMatch;
      setSelectedOwnerId(ownerUuid);
      // Edit mode is now handled inline via PetOwnerForm, not a modal
      return;
    }

    // /dashboard/appointments/{uuid}
    const appointmentMatch = path.match(/\/dashboard\/appointments\/([a-f0-9-]+)/);
    if (appointmentMatch) {
      const [, appointmentUuid] = appointmentMatch;
      setSelectedAppointmentId(appointmentUuid);
      setIsAppointmentModalOpen(true);
      return;
    }

    // /dashboard/pets/new?owner_id={uuid} - Creating a new pet
    if (path.includes('/pets/new')) {
      const searchParams = new URLSearchParams(location.search);
      const ownerIdFromQuery = searchParams.get('owner_id');
      if (ownerIdFromQuery) {
        setSelectedOwnerId(ownerIdFromQuery);
      }
      return;
    }

    // /dashboard/pets/{uuid}
    const petMatch = path.match(/\/dashboard\/pets\/([a-f0-9-]+)/);
    if (petMatch) {
      const [, uuid] = petMatch;
      setSelectedPetId(uuid);
      // Don't return here as we might be on a sub-route (like medical records) that needs further processing? 
      // Actually, we just need to set the ID.
    }
  }, [location.pathname, location.search]);

  // Fetch owner and pet names for breadcrumbs
  useEffect(() => {
    const fetchBreadcrumbNames = async () => {
      // Fetch owner name
      if (selectedOwnerId) {
        try {
          const response = await fetch(API_ENDPOINTS.PET_OWNERS.GET(selectedOwnerId));
          if (response.ok) {
            const data = await response.json();
            setSelectedOwnerName(data.full_name);
          }
        } catch (err) {
          console.error('Error fetching owner name:', err);
        }
      } else {
        setSelectedOwnerName(null);
      }

      // Fetch pet name and owner info
      // Use petUuid from URL if available, otherwise fall back to selectedPetId
      const targetPetId = petUuid || selectedPetId;

      if (targetPetId) {
        console.log('Fetching pet name:', targetPetId);
        try {
          const response = await fetch(API_ENDPOINTS.PETS.GET(targetPetId));
          if (response.ok) {
            const data = await response.json();
            const petName = data.display_name || data.name;
            setSelectedPetName(petName);

            // Update recent pets list immediately after fetching pet data
            if (petName && targetPetId) {
              const storedPets = localStorage.getItem('helppet_recent_pets');
              const pets: { id: string; name: string }[] = storedPets ? JSON.parse(storedPets) : [];

              // Check if already exists
              const existingIndex = pets.findIndex(p => p.id === targetPetId);

              if (existingIndex === -1) {
                // New pet (not in list): Add to end
                pets.push({ id: targetPetId, name: petName });

                // Remove oldest from start if > 5
                if (pets.length > 5) {
                  pets.shift();
                }

                console.log('Added pet to recent list:', petName, pets);
                setRecentPets(pets);
                localStorage.setItem('helppet_recent_pets', JSON.stringify(pets));
              } else {
                // Pet already exists: Update the name in case it changed
                pets[existingIndex].name = petName;
                console.log('Updated pet name in recent list:', petName, pets);
                setRecentPets(pets);
                localStorage.setItem('helppet_recent_pets', JSON.stringify(pets));
              }
            }

            // If we don't have owner info yet, get it from the pet
            if (!selectedOwnerId && data.owner_id) {
              setSelectedOwnerId(data.owner_id);
              if (data.owner?.full_name) {
                setSelectedOwnerName(data.owner.full_name);
              }
            }
          }
        } catch (err) {
          console.error('Error fetching pet name:', err);
        }
      } else {
        setSelectedPetName(null);
      }
    };

    fetchBreadcrumbNames();
  }, [selectedOwnerId, selectedPetId, petUuid]);

  // Fetch latest medical record for the selected pet
  useEffect(() => {
    const fetchLatestMedicalRecord = async () => {
      const targetPetId = petUuid || selectedPetId;
      if (!targetPetId) {
        setLatestMedicalRecordId(null);
        return;
      }

      try {
        const token = localStorage.getItem('access_token');
        const response = await fetch(API_ENDPOINTS.MEDICAL_RECORDS.HISTORY(targetPetId), {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          // Find the current record, or default to the first one if available
          const currentRecord = data.records?.find((r: any) => r.is_current) || data.records?.[0];
          if (currentRecord) {
            setLatestMedicalRecordId(currentRecord.id);
          } else {
            setLatestMedicalRecordId(null);
          }
        }
      } catch (err) {
        console.error('Error fetching medical records:', err);
        setLatestMedicalRecordId(null);
      }
    };

    fetchLatestMedicalRecord();
  }, [petUuid, selectedPetId]);

  // Load Recent Pets from localStorage on mount
  useEffect(() => {
    if (!petUuid) {
      // If not on a pet page, just load the list from localStorage
      const storedPets = localStorage.getItem('helppet_recent_pets');
      if (storedPets) {
        setRecentPets(JSON.parse(storedPets));
      }
    }
  }, [petUuid]);

  // Remove a pet from the recent pets list
  const removeFromRecentPets = (petIdToRemove: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigation
    const updatedPets = recentPets.filter(pet => pet.id !== petIdToRemove);
    setRecentPets(updatedPets);
    localStorage.setItem('helppet_recent_pets', JSON.stringify(updatedPets));
  };

  // Debounce searchTerm - wait 300ms after user stops typing before triggering API call
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Track previous path to detect initial loads vs search/filter changes
  const prevPathRef = useRef(location.pathname);
  const prevPracticeIdRef = useRef(user?.practice_id);

  // Fetch data on mount and when active section changes
  useEffect(() => {
    if (user?.practice_id) {
      // Show skeleton only when navigating to a new section or on initial load
      const isNewSection = prevPathRef.current !== location.pathname || prevPracticeIdRef.current !== user?.practice_id;
      prevPathRef.current = location.pathname;
      prevPracticeIdRef.current = user?.practice_id;
      fetchData(isNewSection);
    }
  }, [location.pathname, user?.practice_id, calendarDate, calendarView, clientsPage, statusFilter, debouncedSearchTerm, appointmentsTodayFilter, needsAppointmentFilter]);

  // Sync ref with state (critical for avoiding stale closures in debounced callbacks and useEffect)
  useEffect(() => {
    calendarDateRef.current = calendarDate;
  }, [calendarDate]);

  // DEAD SIMPLE: Fetch team members ONCE when user logs in (only if empty)
  useEffect(() => {
    const fetchTeamMembersOnce = async () => {
      if (!user?.practice_id) return;
      if (teamMembers.length > 0) {
        return;
      }

      // Try both token keys for compatibility
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');

      try {
        const members = await fetchCachedTeamMembers(user.practice_id, token);
        
        // Sort so current user is always first
        const sortedMembers = [...members].sort((a, b) => {
          if (a.id === user.id) return -1;
          if (b.id === user.id) return 1;
          return a.full_name.localeCompare(b.full_name);
        });
        
        setTeamMembers(sortedMembers);

        // Initialize with all doctors selected (ONLY if never initialized before)
        if (!isVetSelectionInitialized() && selectedVetIds.size === 0 && sortedMembers.length > 0) {
          const allVetIds = new Set(sortedMembers.map(m => m.id));
          setSelectedVetIds(allVetIds);
        }
      } catch (error) {
        console.error('Failed to fetch team members:', error);
      }
    };

    fetchTeamMembersOnce();
  }, [user?.practice_id, teamMembers.length]); // Run when practice changes OR when teamMembers is empty

  // Persist SHARED vet selection to localStorage whenever it changes
  // This applies to ALL views (appointments, schedule, etc)
  useEffect(() => {
    // Always persist, even if empty (to distinguish between "never set" and "all deselected")
    setStoredVetSelection(STORAGE_KEYS.VET_SELECTION, selectedVetIds);
  }, [selectedVetIds]);

  // Refresh schedule data when selected doctors change
  useEffect(() => {
    if (activeSection === 'schedule' && teamMembers.length > 0) {
      fetchScheduleData();
    }
  }, [selectedScheduleVetIds]);

  // Refresh schedule data when calendar date or view changes
  useEffect(() => {
    if (activeSection === 'schedule' && teamMembers.length > 0 && selectedScheduleVetIds.size > 0) {
      fetchScheduleData();
    }
  }, [calendarDate, calendarView]);

  // Close user dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setShowUserDropdown(false);
      }
    };

    if (showUserDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserDropdown]);


  // Toggle vet selection - SHARED across all views (appointments, schedule, etc)
  const toggleVetSelection = (vetId: string) => {
    setSelectedVetIds(prev => {
      const newSet = new Set(prev);

      // Simple toggle: if it's in the set, remove it. If not, add it.
      if (newSet.has(vetId)) {
        newSet.delete(vetId);
      } else {
        newSet.add(vetId);
      }

      return newSet;
    });
  };

  // Alias for compatibility - both use the same function
  const toggleScheduleVetSelection = toggleVetSelection;

  // Get initials from name
  const getInitials = (name: string): string => {
    if (!name) return '??';

    // Trim and filter out empty parts
    const parts = name.trim().split(' ').filter(part => part.length > 0);

    if (parts.length === 0) return '??';
    if (parts.length === 1) {
      // Single name: take first two characters
      return parts[0].substring(0, 2).toUpperCase();
    }

    // Multiple parts: first letter of first and last name
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  };

  // Wrapper to refresh schedule data with cache invalidation
  const refreshScheduleData = async () => {
    scheduleCache.current = null;
    await fetchScheduleData();
  };

  // Function to fetch schedule data specifically (for refreshing without page reload)
  const fetchScheduleData = async () => {
    if (!user?.practice_id || !user.id) return;

    // CRITICAL FIX: Use ref instead of state to avoid stale closure in debounced callbacks
    const currentCalendarDate = calendarDateRef.current;
    const baseURL = API_BASE_URL;
    const token = localStorage.getItem('access_token');
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Calculate date range based on calendar view
    let startDate: Date;
    let endDate: Date;

    if (calendarView === 'month') {
      // For month view, fetch entire month
      startDate = new Date(currentCalendarDate.getFullYear(), currentCalendarDate.getMonth(), 1);
      endDate = new Date(currentCalendarDate.getFullYear(), currentCalendarDate.getMonth() + 1, 0);
    } else if (calendarView === 'week') {
      // For week view, fetch the visible week
      const dayOfWeek = currentCalendarDate.getDay();
      startDate = new Date(currentCalendarDate);
      startDate.setDate(currentCalendarDate.getDate() - dayOfWeek);
      endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);
    } else {
      // For day view, fetch just that day
      startDate = new Date(currentCalendarDate);
      endDate = new Date(currentCalendarDate);
    }

    // Determine which doctors to fetch for
    const doctorsToFetch = selectedScheduleVetIds.size === 0
      ? []
      : Array.from(selectedScheduleVetIds);

    if (doctorsToFetch.length === 0) {
      setSchedule([]);
      return;
    }

    // Build cache key from request parameters
    const startDateStr = startDate.toISOString().split('T')[0];
    const endDateStr = endDate.toISOString().split('T')[0];
    const vetIdsStr = doctorsToFetch.sort().join(',');
    const cacheKey = `${startDateStr}:${endDateStr}:${vetIdsStr}`;

    // Check cache first (5-minute TTL)
    const now = Date.now();
    if (scheduleCache.current &&
      scheduleCache.current.key === cacheKey &&
      (now - scheduleCache.current.timestamp) < CACHE_TTL) {
      setSchedule(scheduleCache.current.data);
      return;
    }

    const scheduleStartTime = performance.now();

    // Use bulk API endpoint (single request for all vets and dates)

    // Pick a sample vet ID for the path parameter (required by endpoint signature)
    const sampleVetId = doctorsToFetch[0];

    try {
      const url = `${baseURL}/api/v1/scheduling-unix/vet-availability/${sampleVetId}?date=${startDateStr}&end_date=${endDateStr}&vet_ids=${vetIdsStr}&timezone=${timezone}`;

      const scheduleRes = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (scheduleRes.ok) {
        const bulkResponse = await scheduleRes.json();

        // Flatten nested response: {date: {vet_id: [events]}} -> [events]
        const allAvailability: any[] = [];
        if (bulkResponse.data) {
          for (const dateKey in bulkResponse.data) {
            for (const vetKey in bulkResponse.data[dateKey]) {
              const events = bulkResponse.data[dateKey][vetKey];
              allAvailability.push(...events);
            }
          }
        }

        // Cache the results
        scheduleCache.current = {
          data: allAvailability,
          timestamp: Date.now(),
          key: cacheKey
        };

        setSchedule(allAvailability);
      } else {
        console.error('Error fetching schedule:', await scheduleRes.text());
        setSchedule([]);
      }
    } catch (err) {
      console.error('Error fetching schedule:', err);
      setSchedule([]);
    }
  };

  const fetchData = async (showSkeleton = false) => {
    if (!user?.practice_id) return;

    // Only show skeleton on initial load, not on search/filter changes
    if (showSkeleton) {
      setLoading(true);
    }
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      const baseURL = API_BASE_URL;

      switch (activeSection) {
        case 'appointments':
          // GET /api/v1/appointments/practice/{practice_id}/filtered
          // Calculate date range based on calendar view and current date
          // CRITICAL FIX: Use ref instead of state to avoid stale closure
          const currentCalendarDate = calendarDateRef.current;
          let apptStartDate: Date;
          let apptEndDate: Date;

          if (appointmentViewMode === 'swimlanes') {
            // For swim lanes view, show current week with some buffer
            const dayOfWeek = currentCalendarDate.getDay();
            apptStartDate = new Date(currentCalendarDate);
            apptStartDate.setDate(currentCalendarDate.getDate() - dayOfWeek); // Start of week
            apptEndDate = new Date(apptStartDate);
            apptEndDate.setDate(apptStartDate.getDate() + 6); // End of week
          } else if (calendarView === 'month') {
            // For month view, fetch entire month plus a bit of buffer
            apptStartDate = new Date(currentCalendarDate.getFullYear(), currentCalendarDate.getMonth(), 1);
            apptEndDate = new Date(currentCalendarDate.getFullYear(), currentCalendarDate.getMonth() + 1, 0);
          } else if (calendarView === 'week') {
            // For week view, fetch the visible week
            const dayOfWeek = currentCalendarDate.getDay();
            apptStartDate = new Date(currentCalendarDate);
            apptStartDate.setDate(currentCalendarDate.getDate() - dayOfWeek); // Start of week (Sunday)
            apptEndDate = new Date(apptStartDate);
            apptEndDate.setDate(apptStartDate.getDate() + 6); // End of week (Saturday)
          } else {
            // For day view, fetch just that day
            apptStartDate = new Date(currentCalendarDate);
            apptStartDate.setHours(0, 0, 0, 0);
            apptEndDate = new Date(currentCalendarDate);
            apptEndDate.setHours(23, 59, 59, 999);
          }

          // Format dates for API (YYYY-MM-DD)
          const formatDate = (date: Date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          };

          const startDateStr = formatDate(apptStartDate);
          const endDateStr = formatDate(apptEndDate);

          const appointmentsRes = await fetch(
            `${baseURL}/api/v1/appointments/practice/${user.practice_id}/filtered?start_date=${startDateStr}&end_date=${endDateStr}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          if (appointmentsRes.ok) {
            const data = await appointmentsRes.json();
            setAppointments(data);
          }
          break;

        case 'calls':
          // GET /api/v1/call-analysis/practice/{practice_id}/calls
          const callsRes = await fetch(`${baseURL}/api/v1/call-analysis/practice/${user.practice_id}/calls?limit=20&offset=0`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (callsRes.ok) {
            const data = await callsRes.json();
            setCalls(data.calls || []);
          }
          break;

        case 'flow':
          // Patient Flow - fetch ONLY TODAY'S appointments
          // CRITICAL FIX: Use calendarDateRef to match other views (appointments, swimlanes)
          const currentFlowDate = calendarDateRef.current;

          const flowStartDate = new Date(currentFlowDate);
          flowStartDate.setHours(0, 0, 0, 0);
          const flowEndDate = new Date(currentFlowDate);
          flowEndDate.setHours(23, 59, 59, 999);

          const formatFlowDate = (date: Date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          };

          const todayStr = formatFlowDate(flowStartDate);
          const endStr = formatFlowDate(flowEndDate);

          const flowRes = await fetch(
            `${baseURL}/api/v1/appointments/practice/${user.practice_id}/filtered?start_date=${todayStr}&end_date=${endStr}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          if (flowRes.ok) {
            const data = await flowRes.json();
            setAppointments(data);
          }
          break;

        case 'schedule':
          // Use the dedicated schedule fetch function
          await fetchScheduleData();
          break;

        case 'pets':
          // Fetch stats first (for the stats cards)
          const statsRes = await fetch(
            `${API_ENDPOINTS.PET_OWNERS.STATS}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          if (statsRes.ok) {
            const statsData = await statsRes.json();
            setClientsStats(statsData);
          }
          
          // Use paginated endpoint - fetches only what's displayed (25 items)
          const params = new URLSearchParams({
            page: clientsPage.toString(),
            page_size: clientsPageSize.toString(),
          });
          if (statusFilter) params.append('status_filter', statusFilter);
          if (debouncedSearchTerm) params.append('search', debouncedSearchTerm);
          if (appointmentsTodayFilter) params.append('appointments_today', 'true');
          if (needsAppointmentFilter) params.append('needs_appointment', 'true');
          
          const paginatedRes = await fetch(
            `${API_ENDPOINTS.PET_OWNERS.PAGINATED}?${params}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          
          if (paginatedRes.ok) {
            const data = await paginatedRes.json();
            // Transform to match existing owner format
            const transformedOwners = data.items.map((item: any) => ({
              uuid: item.uuid,
              full_name: item.full_name,
              email: item.email,
              phone: item.phone,
              pets: item.pets || [],
            }));
            setOwners(transformedOwners);
            
            // Build CRM map from the same response (partial data for display)
            const crmMap: Record<string, any> = {};
            data.items.forEach((item: any) => {
              crmMap[item.uuid] = {
                pet_owner_id: item.uuid,
                customer_status: item.customer_status,
                last_visit_date: item.last_visit_date,
                next_appointment_date: item.next_appointment_date,
                touch_count: item.touch_count,
              };
            });
            setOwnersCrmData(crmMap);
            
            // Update pagination state
            setClientsTotalPages(data.total_pages);
            setClientsTotal(data.total);
          }
          break;
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Handler for booking appointment from pet card
  const handleBookAppointmentFromPet = (pet: { id: string; name: string }, ownerId: string) => {
    setAppointmentPetId(pet.id);
    setAppointmentOwnerId(ownerId);
    setSelectedSlot(null);
    setIsCreateAppointmentOpen(true);
  };

  // Handler for refreshing CRM data - just refetch with refresh=true flag
  const handleRefreshCrm = async () => {
    setIsRefreshingCrm(true);
    try {
      const token = localStorage.getItem('token');
      // Build params with refresh=true to force cache refresh
      const params = new URLSearchParams({
        page: clientsPage.toString(),
        page_size: clientsPageSize.toString(),
        refresh: 'true',
      });
      if (statusFilter) params.append('status_filter', statusFilter);
      if (debouncedSearchTerm) params.append('search', debouncedSearchTerm);
      if (appointmentsTodayFilter) params.append('appointments_today', 'true');
      if (needsAppointmentFilter) params.append('needs_appointment', 'true');
      
      const response = await fetch(
        `${API_ENDPOINTS.PET_OWNERS.PAGINATED}?${params}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        const transformedOwners = data.items.map((item: any) => ({
          uuid: item.uuid,
          full_name: item.full_name,
          email: item.email,
          phone: item.phone,
          pets: item.pets || [],
        }));
        setOwners(transformedOwners);
        
        const crmMap: Record<string, any> = {};
        data.items.forEach((item: any) => {
          crmMap[item.uuid] = {
            pet_owner_id: item.uuid,
            customer_status: item.customer_status,
            last_visit_date: item.last_visit_date,
            next_appointment_date: item.next_appointment_date,
            touch_count: item.touch_count,
          };
        });
        setOwnersCrmData(crmMap);
        setClientsTotalPages(data.total_pages);
        setClientsTotal(data.total);
      }
    } catch (error) {
      console.error('Failed to refresh CRM:', error);
    } finally {
      setIsRefreshingCrm(false);
    }
  };

  const getButtonAction = () => {
    switch (activeSection) {
      case 'appointments': return { label: 'New Appointment', action: () => { setAppointmentPetId(null); setAppointmentOwnerId(null); setSelectedSlot(null); setIsCreateAppointmentOpen(true); } };
      case 'flow': return {
        label: 'Create', action: () => {
          // Trigger the walk-in modal via the exposed handler
          if ((window as any)._patientFlowCreateHandler) {
            (window as any)._patientFlowCreateHandler();
          }
        }
      };
      case 'calls': return { label: 'View All', action: () => { } };
      case 'schedule': return { label: 'Add Availability', action: () => { setSelectedScheduleSlot(null); setIsCreateScheduleOpen(true); } };
      case 'pets':
        // Labs view
        if (location.pathname.includes('/labs')) {
          return {
            label: '+ Order Lab',
            action: () => {
              // Trigger the lab order modal via the exposed handler
              if ((window as any)._labsOrderHandler) {
                (window as any)._labsOrderHandler();
              }
            }
          };
        }
        // Medications view
        if (location.pathname.includes('/medications')) {
          return {
            label: '+ Add Medication',
            action: () => {
              // Trigger the medication search modal via the exposed handler
              if ((window as any)._medicationsAddHandler) {
                (window as any)._medicationsAddHandler();
              }
            }
          };
        }
        // Billing view
        if (location.pathname.includes('/billing')) {
          return null; // No CTA button for billing view
        }
        // Immunizations view - check this FIRST before generic "Edit Pet"
        if (location.pathname.includes('/immunizations')) {
          return {
            label: '+ Immunization',
            action: () => {
              setSelectedImmunization(undefined);
              setIsImmunizationModalOpen(true);
            }
          };
        }
        // Medical Summary view - show Edit and View History buttons
        if (location.pathname.includes('/medical-records/')) {
          // If on history page or create page, no button needed
          if (location.pathname.includes('/history') || location.pathname.includes('/create')) {
            return null;
          }
          // If viewing a specific record (not editing), show Edit Record button
          if (!location.pathname.includes('/edit') && latestMedicalRecordId) {
            return {
              label: 'Edit Record',
              action: () => navigate(`/dashboard/pets/${selectedPetId}/medical-records/create?copy_from_id=${latestMedicalRecordId}`)
            };
          }
          return null;
        }
        // Hide ALL buttons when creating/editing a pet or editing a reminder
        if (location.pathname.includes('/pets/new') || 
            (location.pathname.includes('/pets/') && location.pathname.includes('/edit')) ||
            location.pathname.includes('/reminders/')) {
          return null;
        }
        // Forms view - show + Form button
        if (location.pathname.includes('/forms')) {
          return {
            label: '+ Form',
            action: () => {
              // Trigger the form modal via the exposed handler
              if ((window as any)._formsAddHandler) {
                (window as any)._formsAddHandler();
              }
            }
          };
        }
        // If viewing a specific pet (and not already editing or viewing history/records)
        if (selectedPetId && !location.pathname.includes('/edit') && !location.pathname.includes('/medical-records/')) {
          return {
            label: 'Edit Basic Info',
            action: () => navigate(`/dashboard/pets/${selectedPetId}/edit`)
          };
        }
        // If viewing an owner (and not a pet)
        if (selectedOwnerId && !selectedPetId) {
          return {
            label: 'Add Pet',
            action: () => {
              navigate(`/dashboard/pets/new?owner_id=${selectedOwnerId}`);
            }
          };
        }
        // Default view (list of owners)
        if (!selectedOwnerId && !selectedPetId) {
          return { label: 'Add Owner', action: () => navigate('/dashboard/pet_owners/new') };
        }
        return null;
      case 'broadcast': {
        // Don't show create button in detail views
        const viewInfo = getBroadcastViewInfo(searchParams);
        if (viewInfo.isDetailView) {
          return null;
        }
        const broadcastTab = (searchParams.get('tab') as BroadcastTab) || 'compose';
        const tabInfo = BROADCAST_TABS.find(t => t.key === broadcastTab);
        if (tabInfo?.createLabel) {
          return {
            label: tabInfo.createLabel,
            action: () => broadcastRef.current?.openCreate()
          };
        }
        return null;
      }
      case 'set-and-forget': {
        const currentTab = searchParams.get('tab') || 'reminders';
        // No create button for logs tab
        if (currentTab === 'logs') return null;
        let label = '+ Reminder';
        if (currentTab === 'triggers') label = '+ Trigger';
        if (currentTab === 'overdue') label = '+ Overdue Reminder';
        return {
          label,
          action: () => setAndForgetRef.current?.openCreate()
        };
      }
      case 'templates': {
        return {
          label: '+ Template',
          action: () => templatesManagerRef.current?.openCreate()
        };
      }
      case 'audience': {
        const listId = searchParams.get('listId');
        if (listId) {
          // In detail view - show Edit button
          return {
            label: 'Edit',
            action: () => audienceManagerRef.current?.openEdit?.()
          };
        }
        return {
          label: '+ Audience',
          action: () => audienceManagerRef.current?.openCreate()
        };
      }
      default: return { label: 'Create', action: () => { } };
    }
  };

  // Handle nested routes (e.g., /dashboard/appointments/new - full page forms only)
  const isFullPageRoute = location.pathname.includes('/appointments/new');

  // If it's a full-page route, don't render the dashboard chrome
  if (isFullPageRoute) {
    return (
      <Routes>
        <Route path="appointments/new" element={<AppointmentForm />} />
      </Routes>
    );
  }

  // Handle updating appointment status (for Patient Flow drag-and-drop)
  const handleUpdateAppointmentStatus = async (appointmentId: string, newStatus: string) => {
    try {
      const token = localStorage.getItem('access_token');
      const baseURL = API_BASE_URL;

      const response = await fetch(`${baseURL}/api/v1/appointments/${appointmentId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update appointment status');
      }

      // Refresh appointments to show updated status
      fetchData();
    } catch (error) {
      console.error('Failed to update appointment status:', error);
      throw error; // Re-throw so PatientFlowView can handle the error
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  // Handle sending an invitation
  const handleSendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsInviting(true);
    setInviteError(null);
    setInviteSuccess(null);

    try {
      const token = localStorage.getItem('access_token');
      const baseURL = API_BASE_URL;

      const response = await fetch(`${baseURL}/api/v1/practices/${user?.practice_id}/invites`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: inviteEmail })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send invitation');
      }

      setInviteSuccess('Invitation sent successfully!');
      setInviteEmail('');

      // Invalidate team members cache so next fetch gets fresh data
      invalidateTeamMembersCache();

      setTimeout(() => {
        setShowInviteModal(false);
        setInviteSuccess(null);
        fetchData(); // Refresh team data
      }, 2000);
    } catch (error: any) {
      setInviteError(error.message || 'Failed to send invitation');
    } finally {
      setIsInviting(false);
    }
  };

  // Handle revoking an invitation
  const handleRevokeInvite = async () => {
    if (!revokeInviteId) return;

    try {
      const token = localStorage.getItem('access_token');
      const baseURL = API_BASE_URL;

      const response = await fetch(`${baseURL}/api/v1/practices/${user?.practice_id}/invites/${revokeInviteId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to revoke invitation');
      }

      setPendingInvites(pendingInvites.filter(inv => inv.id !== revokeInviteId));
      setRevokeInviteId(null);
    } catch (error: any) {
      console.error('Error revoking invitation:', error);
      setError(error.message || 'Failed to revoke invitation');
      setRevokeInviteId(null);
    }
  };

  // Handle removing a team member
  const handleRemoveMember = async () => {
    if (!removeMemberId) return;

    try {
      const token = localStorage.getItem('access_token');
      const baseURL = API_BASE_URL;

      const response = await fetch(`${baseURL}/api/v1/practices/${user?.practice_id}/members/${removeMemberId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to remove team member');
      }

      // Invalidate team members cache so next fetch gets fresh data
      invalidateTeamMembersCache();

      setTeamMembers(teamMembers.filter(m => m.id !== removeMemberId));
      setRemoveMemberId(null);
    } catch (error: any) {
      console.error('Error removing team member:', error);
      setError(error.message || 'Failed to remove team member');
      setRemoveMemberId(null);
    }
  };

  const formatDuration = (start: string, end: string) => {
    if (!start || !end) return '-';
    try {
      const duration = Math.abs(new Date(end).getTime() - new Date(start).getTime()) / 1000;
      const minutes = Math.floor(duration / 60);
      const seconds = Math.floor(duration % 60);
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    } catch {
      return '-';
    }
  };

  // No longer need to check for nested routes - we'll render them as overlays

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-dark-bg-primary overflow-hidden">
      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white dark:bg-dark-bg-secondary rounded-lg shadow-lg border border-gray-200 dark:border-dark-border"
      >
        {isMobileMenuOpen ? (
          <X className="w-6 h-6 text-gray-700 dark:text-dark-text-primary" />
        ) : (
          <svg className="w-6 h-6 text-gray-700 dark:text-dark-text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        )}
      </button>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Left Sidebar */}
      <div className={`
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
        fixed lg:relative
        z-40
        w-60 h-full
        bg-white dark:bg-dark-bg-secondary border-r border-gray-200 dark:border-dark-border flex flex-col
        transition-transform duration-300 ease-in-out
      `}>
        {/* Logo/Workspace */}
        <div className="p-4 border-b border-gray-200 dark:border-dark-border relative" ref={userDropdownRef}>
          <button
            className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-gray-50 dark:hover:bg-dark-bg-hover cursor-pointer transition-colors"
            onClick={() => setShowUserDropdown(!showUserDropdown)}
          >
            <div className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center bg-white dark:bg-dark-bg-tertiary border border-gray-200 dark:border-dark-border">
              <img
                src="/logo_clear_back.png"
                alt={APP_NAME}
                className="w-full h-full object-contain p-1"
              />
            </div>
            <div className="flex-1 min-w-0 text-left">
              <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">{APP_NAME}</div>
              <div className="text-xs text-gray-500 dark:text-dark-text-muted">{user?.full_name || 'User'}</div>
            </div>
            {/* Three dots menu icon */}
            <svg
              className="w-5 h-5 text-gray-600 dark:text-dark-text-secondary flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
              />
            </svg>
          </button>

          {/* Dropdown Menu */}
          {showUserDropdown && (
            <div
              className="absolute left-4 right-4 top-full bg-white dark:bg-dark-bg-tertiary border border-gray-200 dark:border-dark-border rounded-xl shadow-2xl z-50 mt-1 overflow-hidden"
            >
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-4 py-3 text-base text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors font-medium"
              >
                <LogOut className="w-5 h-5" />
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex-1 overflow-y-auto py-4">
          {/* CLINIC OPERATIONS Section */}
          <div className="px-3 mb-4">
            <div className="text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase mb-2 px-2">Clinic Operations</div>
            {navigationItems.filter(item => item.section === 'CLINIC OPERATIONS').map((item) => (
              <button
                key={item.key}
                onClick={() => {
                  navigate(item.path);
                  setIsMobileMenuOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${activeSection === item.key
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 dark:text-dark-text-secondary hover:bg-gray-100 dark:hover:bg-dark-bg-hover'
                  }`}
              >
                {item.icon}
                <span>{item.label}</span>
              </button>
            ))}
          </div>

          {/* CRM Section */}
          <div className="px-3 mb-4">
            <div className="text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase mb-2 px-2">CRM</div>
            {navigationItems.filter(item => item.section === 'CRM').map((item) => (
              <React.Fragment key={item.key}>
                <button
                  onClick={() => {
                    navigate(item.path);
                    setIsMobileMenuOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${activeSection === item.key && !petUuid
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-700 dark:text-dark-text-secondary hover:bg-gray-100 dark:hover:bg-dark-bg-hover'
                    }`}
                >
                  {item.icon}
                  <span className="flex-1 text-left">{item.label}</span>
                  {item.key === 'pets' && (petUuid || recentPets.length > 0) && (
                    <div
                      role="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsPetsSectionOpen(!isPetsSectionOpen);
                      }}
                      className="p-1 hover:bg-gray-200 dark:hover:bg-dark-bg-hover rounded"
                    >
                      {isPetsSectionOpen ? (
                        <ChevronDown className="w-4 h-4 text-gray-400 dark:text-dark-text-muted" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-400 dark:text-dark-text-muted" />
                      )}
                    </div>
                  )}
                </button>

                {/* Recent Pets List */}
                {item.key === 'pets' && isPetsSectionOpen && (petUuid || recentPets.length > 0) && (
                  <div className="ml-4 mt-1 space-y-1 border-l-2 border-gray-200 dark:border-dark-border pl-2">
                    {recentPets.map((pet) => {
                      const isCurrentPet = pet.id === petUuid;

                      return (
                        <div
                          key={pet.id}
                          className={`group w-full flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors cursor-pointer ${isCurrentPet
                            ? 'text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20'
                            : 'text-gray-600 dark:text-dark-text-secondary hover:bg-gray-50 dark:hover:bg-dark-bg-hover'
                            }`}
                          onClick={() => navigate(`/dashboard/pets/${pet.id}`)}
                        >
                          <span className="flex-1 text-left truncate">{pet.name}</span>
                          <button
                            onClick={(e) => removeFromRecentPets(pet.id, e)}
                            className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-opacity"
                            title="Remove from recent"
                          >
                            <X className="w-3 h-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* COMMUNICATIONS Section - Collapsible */}
          <div className="px-3 mb-4">
            <button
              onClick={() => toggleSectionCollapse('COMMUNICATIONS')}
              className="w-full flex items-center justify-between text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase mb-2 px-2 hover:text-gray-700 dark:hover:text-dark-text-secondary transition-colors"
            >
              <span>Communications</span>
              {collapsedSections.has('COMMUNICATIONS') ? (
                <ChevronRight className="w-3.5 h-3.5" />
              ) : (
                <ChevronDown className="w-3.5 h-3.5" />
              )}
            </button>
            {!collapsedSections.has('COMMUNICATIONS') && navigationItems.filter(item => item.section === 'COMMUNICATIONS').map((item) => (
              <button
                key={item.key}
                onClick={() => {
                  navigate(item.path);
                  setIsMobileMenuOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${activeSection === item.key
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 dark:text-dark-text-secondary hover:bg-gray-100 dark:hover:bg-dark-bg-hover'
                  }`}
              >
                {item.icon}
                <span>{item.label}</span>
              </button>
            ))}
          </div>

          {/* FRONT DESK Section */}
          <div className="px-3 mb-4">
            <div className="text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase mb-2 px-2">Front Desk</div>
            {navigationItems.filter(item => item.section === 'FRONT DESK').map((item) => (
              <button
                key={item.key}
                onClick={() => {
                  navigate(item.path);
                  setIsMobileMenuOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${activeSection === item.key
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 dark:text-dark-text-secondary hover:bg-gray-100 dark:hover:bg-dark-bg-hover'
                  }`}
              >
                {item.icon}
                <span>{item.label}</span>
              </button>
            ))}
          </div>

          {/* ADMIN Section (ADMIN users only) */}
          {isAdmin && navigationItems.filter(item => item.section === 'ADMIN').length > 0 && (
            <div className="px-3 mb-4">
              <div className="text-xs font-semibold text-red-600 dark:text-red-400 uppercase mb-2 px-2">Admin</div>
              {navigationItems.filter(item => item.section === 'ADMIN').map((item) => (
                <button
                  key={item.key}
                  onClick={() => {
                    navigate(item.path);
                    setIsMobileMenuOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${activeSection === item.key
                    ? 'bg-red-600 text-white'
                    : 'text-gray-700 dark:text-dark-text-secondary hover:bg-red-50 dark:hover:bg-red-900/20'
                    }`}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Bottom Section */}
        <div className="p-3 border-t border-gray-200 dark:border-dark-border space-y-3">
          <Link to="/settings" className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-gray-700 dark:text-dark-text-secondary hover:bg-gray-100 dark:hover:bg-dark-bg-hover transition-colors">
            <Settings className="w-4 h-4" />
            <span>Settings</span>
          </Link>
          <button
            onClick={() => setShowHelpModal(true)}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-gray-700 dark:text-dark-text-secondary hover:bg-gray-100 dark:hover:bg-dark-bg-hover transition-colors"
          >
            <HelpCircle className="w-4 h-4" />
            <span>Help</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden w-full lg:w-auto">
        {/* Top Bar */}
        <div className="bg-white dark:bg-dark-bg-secondary border-b border-gray-200 dark:border-dark-border px-4 lg:px-6 py-4 pl-16 lg:pl-6">
          {/* First row: Title/Breadcrumbs + Action buttons */}
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-shrink">
              {/* Hide title on pet pages and broadcast/set-and-forget/templates/audience pages - breadcrumbs provide context */}
              {!(activeSection === 'pets' && petUuid) && activeSection !== 'broadcast' && activeSection !== 'set-and-forget' && activeSection !== 'templates' && activeSection !== 'audience' && (
                <h1 className="text-xl lg:text-2xl font-semibold text-gray-900 dark:text-dark-text-primary truncate mb-2">
                  {location.pathname.includes('/immunizations') ? 'Immunizations' :
                   location.pathname.includes('/medications') ? 'Medications' :
                   location.pathname.includes('/labs') ? 'Labs' :
                   location.pathname.includes('/billing') ? 'Billing' :
                   location.pathname.includes('/notifications') ? 'Notifications' :
                   location.pathname.includes('/forms') ? 'Forms' :
                   location.pathname.includes('/medical-records') ? 'Medical Summary' :
                   navigationItems.find(item => item.key === activeSection)?.label || 'Dashboard'}
                </h1>
              )}

              {/* Breadcrumbs for ALL Broadcast pages */}
              {activeSection === 'broadcast' && (() => {
                const viewInfo = getBroadcastViewInfo(searchParams);
                const broadcastTab = (searchParams.get('tab') as BroadcastTab) || 'compose';
                const tabInfo = BROADCAST_TABS.find(t => t.key === broadcastTab);
                
                if (viewInfo.isDetailView) {
                  // Detail view: Broadcast > Tab > Detail Title
                  const parentTabInfo = BROADCAST_TABS.find(t => t.key === viewInfo.parentTab);
                  
                    return (
                      <Breadcrumb
                        items={[
                          { label: 'Broadcast', href: '/dashboard/broadcast' },
                          { label: parentTabInfo?.label || viewInfo.parentTab, href: `/dashboard/broadcast?tab=${viewInfo.parentTab}` },
                        { label: viewInfo.title, isActive: true },
                      ]}
                      className=""
                    />
                  );
                } else {
                  // Tab view: Broadcast > Tab
                  return (
                    <Breadcrumb
                      items={[
                        { label: 'Broadcast', href: '/dashboard/broadcast' },
                        { label: tabInfo?.label || 'Compose', isActive: true }
                        ]}
                        className=""
                      />
                    );
                  }
              })()}

              {/* Breadcrumbs for Set And Forget pages */}
              {activeSection === 'set-and-forget' && (() => {
                const info = getSetAndForgetBreadcrumbInfo(location.pathname, searchParams);
                let activeTabLabel = 'Appointment Reminders';
                if (info.activeTab === 'triggers') activeTabLabel = 'Form Triggers';
                if (info.activeTab === 'overdue') activeTabLabel = 'Overdue Vaccines';
                if (info.activeTab === 'logs') activeTabLabel = 'Logs';
                
                if (info.mode === 'new') {
                  // Creating new: Set And Forget > Tab > New
                  return (
                    <Breadcrumb
                      items={[
                        { label: 'Set And Forget', href: '/dashboard/set-and-forget' },
                        { label: activeTabLabel, href: `/dashboard/set-and-forget?tab=${info.activeTab}` },
                        { label: info.type === 'trigger' ? 'New Trigger' : info.type === 'overdue' ? 'New Overdue Reminder' : 'New Reminder', isActive: true },
                      ]}
                      className=""
                    />
                  );
                } else if (info.mode === 'edit') {
                  // Editing: Set And Forget > Tab > Edit
                  return (
                    <Breadcrumb
                      items={[
                        { label: 'Set And Forget', href: '/dashboard/set-and-forget' },
                        { label: activeTabLabel, href: `/dashboard/set-and-forget?tab=${info.activeTab}` },
                        { label: info.type === 'trigger' ? 'Edit Trigger' : info.type === 'overdue' ? 'Edit Overdue Reminder' : 'Edit Reminder', isActive: true },
                      ]}
                      className=""
                    />
                  );
                } else {
                  // List view: Set And Forget > Tab
                  return (
                    <Breadcrumb
                      items={[
                        { label: 'Set And Forget', href: '/dashboard/set-and-forget' },
                        { label: activeTabLabel, isActive: true },
                      ]}
                      className=""
                    />
                  );
                }
              })()}

              {/* Breadcrumbs for Templates section */}
              {activeSection === 'templates' && (() => {
                const path = location.pathname;
                const mode = searchParams.get('mode');
                const isVisualBuilder = mode === 'visual';
                
                // /dashboard/templates/new - Create new template
                if (path.endsWith('/templates/new')) {
                  return (
                    <Breadcrumb
                      items={[
                        { label: 'Templates', href: '/dashboard/templates' },
                        { label: 'New Template', isActive: true },
                      ]}
                      className=""
                    />
                  );
                }
                
                // /dashboard/templates/:id - Edit existing template
                const templateIdMatch = path.match(/\/templates\/([a-zA-Z0-9-]+)$/);
                if (templateIdMatch && templateIdMatch[1] !== 'new') {
                  return (
                    <Breadcrumb
                      items={[
                        { label: 'Templates', href: '/dashboard/templates' },
                        { label: selectedTemplateName || 'Loading...', isActive: true },
                      ]}
                      className=""
                    />
                  );
                }
                
                // List view
                return (
                  <Breadcrumb
                    items={[
                      { label: 'Templates', isActive: true },
                    ]}
                    className=""
                  />
                );
              })()}

              {/* Breadcrumbs for Audience section */}
              {activeSection === 'audience' && (() => {
                const listId = searchParams.get('listId');
                const isEditMode = searchParams.get('edit') === 'true';
                
                if (listId) {
                  // Detail view: Audience Manager > [List Name] > Edit (if editing)
                  const items = [
                    { label: 'Audience Manager', href: '/dashboard/audience' },
                    { label: selectedAudienceName || 'Loading...', href: isEditMode ? `/dashboard/audience?listId=${listId}` : undefined, isActive: !isEditMode },
                  ];
                  if (isEditMode) {
                    items.push({ label: 'Edit', href: undefined, isActive: true });
                  }
                  return (
                    <Breadcrumb
                      items={items}
                      className=""
                    />
                  );
                }
                
                // List view
                return (
                  <Breadcrumb
                    items={[
                      { label: 'Audience Manager', isActive: true },
                    ]}
                    className=""
                  />
                );
              })()}

              {/* Breadcrumbs for Pets section */}
              {activeSection === 'pets' && (selectedOwnerId || selectedPetId || location.pathname.includes('/pets/new')) && (
                <Breadcrumb
                  items={[
                    { label: 'Pet Owners', href: '/dashboard/pet_owners' },
                    ...(selectedOwnerId && selectedOwnerName ? [{
                      label: selectedOwnerName,
                      href: `/dashboard/pet_owners/${selectedOwnerId}`,
                      isActive: !selectedPetId && !location.pathname.includes('/edit') && !location.pathname.includes('/pets/new')
                    }] : []),
                    ...(selectedPetId && selectedPetName ? [{
                      label: selectedPetName,
                      href: `/dashboard/pets/${selectedPetId}`,
                      isActive: !location.pathname.includes('/medical-records/') && 
                               !location.pathname.includes('/edit') && 
                               !location.pathname.includes('/immunizations') && 
                               !location.pathname.includes('/labs') &&
                               !location.pathname.includes('/billing') &&
                               !location.pathname.includes('/forms') &&
                               !location.pathname.includes('/medications') &&
                               !location.pathname.includes('/reminders/')
                    }] : []),
                    ...(location.pathname.includes('/edit') && !location.pathname.includes('/medical-records/') && !location.pathname.includes('/reminders/') ? [{
                      label: 'Edit Profile',
                      isActive: true
                    }] : []),
                    ...(location.pathname.includes('/pets/new') ? [{
                      label: 'Add Pet',
                      isActive: true
                    }] : []),
                    ...(location.pathname.includes('/pets/create') ? [{
                      label: 'Create Pet',
                      isActive: true
                    }] : []),
                    ...(location.pathname.includes('/medical-records/') ? [{
                      label: 'Medical Summary',
                      href: `/dashboard/pets/${selectedPetId}/medical-records/history`,
                      isActive: location.pathname.endsWith('/history')
                    }] : []),
                    ...(location.pathname.includes('/medical-records/create') ? [{
                      label: 'Edit',
                      isActive: true
                    }] : []),
                    ...(location.pathname.includes('/medical-records/') && !location.pathname.endsWith('/history') && !location.pathname.endsWith('/create') ? [{
                      label: location.pathname.includes('/edit') ? 'Edit' : 'View',
                      isActive: true
                    }] : []),
                    ...(location.pathname.includes('/immunizations') ? [{
                      label: 'Immunizations',
                      isActive: true
                    }] : []),
                    ...(location.pathname.includes('/labs') ? [{
                      label: 'Labs',
                      isActive: true
                    }] : []),
                    ...(location.pathname.includes('/forms') ? [{
                      label: 'Forms',
                      isActive: true
                    }] : []),
                    ...(location.pathname.includes('/billing') ? [{
                      label: 'Billing',
                      isActive: true
                    }] : []),
                    ...(location.pathname.includes('/medications') ? [{
                      label: 'Medications',
                      isActive: true
                    }] : []),
                    ...(reminderId ? [{
                      label: 'Edit Reminder',
                      isActive: true
                    }] : [])
                  ]}
                  className=""
                />
              )}

            </div>
            <div className="flex items-center gap-2 lg:gap-3 flex-shrink-0 h-10">
              {/* Show avatar circles in appointments, schedule, or flow view */}
              {(activeSection === 'appointments' || activeSection === 'flow') ? (
                <div className="flex items-center gap-2">
                  {teamMembers.map((member: TeamMember, idx: number) => {
                    const isSelected = selectedVetIds.has(member.id);
                    const colors = [
                      'bg-blue-500',
                      'bg-emerald-500',
                      'bg-purple-500',
                      'bg-orange-500',
                      'bg-pink-500',
                      'bg-indigo-500',
                      'bg-teal-500',
                      'bg-rose-500',
                    ];
                    const color = colors[idx % colors.length];

                    // Hide the label (swim lane) but keep the avatar clickable
                    return (
                      <button
                        key={member.id}
                        onClick={() => toggleVetSelection(member.id)}
                        className={`flex items-center justify-center transition-opacity ${isSelected ? 'opacity-100' : 'opacity-40 hover:opacity-70'}`}
                        title={`${member.full_name} (click to ${isSelected ? 'hide' : 'show'})`}
                      >
                        <UserAvatar
                          photoUrl={member.photo_url}
                          fullName={member.full_name}
                          size="md"
                          colorIndex={idx}
                          className={isSelected ? 'ring-2 ring-inset ring-blue-500 rounded-full' : ''}
                        />
                      </button>
                    );
                  })}
                </div>
              ) : activeSection === 'schedule' ? (
                <div className="flex items-center gap-2">
                  {teamMembers.map((member: TeamMember, idx: number) => {
                    const isSelected = selectedScheduleVetIds.has(member.id);
                    const colors = [
                      'bg-blue-500',
                      'bg-emerald-500',
                      'bg-purple-500',
                      'bg-orange-500',
                      'bg-pink-500',
                      'bg-indigo-500',
                      'bg-teal-500',
                      'bg-rose-500',
                    ];
                    const color = colors[idx % colors.length];

                    return (
                      <button
                        key={member.id}
                        onClick={() => toggleScheduleVetSelection(member.id)}
                        className={`flex items-center justify-center transition-opacity ${isSelected ? 'opacity-100' : 'opacity-40 hover:opacity-70'}`}
                        title={`${member.full_name} (click to ${isSelected ? 'hide' : 'show'})`}
                      >
                        <UserAvatar
                          photoUrl={member.photo_url}
                          fullName={member.full_name}
                          size="md"
                          colorIndex={idx}
                          className={isSelected ? 'ring-2 ring-inset ring-blue-500 rounded-full' : ''}
                        />
                      </button>
                    );
                  })}
                </div>
              ) : !petUuid && !selectedOwnerId && !location.pathname.includes('/immunizations') && !location.pathname.includes('/labs') && !location.pathname.includes('/medications') && !location.pathname.includes('/billing') && !location.pathname.includes('/medical-records') && activeSection !== 'broadcast' && activeSection !== 'set-and-forget' && activeSection !== 'templates' && activeSection !== 'audit-log' ? (
                <div className="relative hidden md:block">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-dark-text-muted" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setClientsPage(1); }}
                    className="pl-10 pr-4 py-2 text-sm bg-white dark:bg-dark-bg-tertiary border border-gray-200 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-dark-text-primary placeholder-gray-400 dark:placeholder-dark-text-muted w-48 lg:w-64"
                  />
                </div>
              ) : null}
              {/* Edit Owner button - show when viewing a pet owner (not when adding/editing pet or editing owner) */}
              {activeSection === 'pets' && selectedOwnerId && !selectedPetId && !location.pathname.includes('/pets/new') && !location.pathname.includes('/edit') && (
                <button
                  onClick={() => navigate(`/dashboard/pet_owners/${selectedOwnerId}/edit`)}
                  className="h-10 inline-flex items-center bg-gray-100 dark:bg-dark-bg-tertiary text-gray-700 dark:text-dark-text-secondary px-3 lg:px-4 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-dark-bg-hover transition-colors whitespace-nowrap border border-gray-300 dark:border-dark-border"
                >
                  <span className="hidden sm:inline">Edit {selectedOwnerName?.split(' ')[0] || 'Owner'}</span>
                  <Edit className="w-5 h-5 sm:hidden" />
                </button>
              )}
              {/* Refresh CRM and Import CSV buttons - show on CRM list view */}
              {activeSection === 'pets' && !selectedOwnerId && !selectedPetId && (
                <>
                  <button
                    onClick={handleRefreshCrm}
                    disabled={isRefreshingCrm}
                    className="h-10 inline-flex items-center gap-2 bg-gray-100 dark:bg-dark-bg-tertiary text-gray-700 dark:text-dark-text-secondary px-3 lg:px-4 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-dark-bg-hover transition-colors whitespace-nowrap border border-gray-300 dark:border-dark-border disabled:opacity-50"
                    title="Refresh CRM Data"
                  >
                    <RefreshCw className={`w-4 h-4 ${isRefreshingCrm ? 'animate-spin' : ''}`} />
                  </button>
                  <button
                    onClick={() => setIsCsvImportOpen(true)}
                    className="h-10 inline-flex items-center gap-2 bg-gray-100 dark:bg-dark-bg-tertiary text-gray-700 dark:text-dark-text-secondary px-3 lg:px-4 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-dark-bg-hover transition-colors whitespace-nowrap border border-gray-300 dark:border-dark-border"
                  >
                    <Upload className="w-4 h-4" />
                    <span className="hidden sm:inline">Import CSV</span>
                  </button>
                </>
              )}
              {getButtonAction() && (
                <button
                  onClick={getButtonAction()!.action}
                  className="h-10 inline-flex items-center bg-blue-600 text-white px-3 lg:px-4 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors whitespace-nowrap"
                >
                  <span className="hidden sm:inline">{getButtonAction()!.label}</span>
                  <Plus className="w-5 h-5 sm:hidden" />
                </button>
              )}
              {/* View Message button - show when viewing campaign details */}
              {activeSection === 'broadcast' && searchParams.get('campaignId') && (
                <button
                  onClick={() => setShowCampaignPreviewModal(true)}
                  className="h-10 inline-flex items-center gap-2 bg-blue-600 text-white px-4 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors whitespace-nowrap"
                >
                  <Eye className="w-4 h-4" />
                  <span className="hidden sm:inline">View Message</span>
                </button>
              )}
              {/* Team Chat Button - keep in top-right header */}
              {user?.practice_id && (
                <>
                  <NotificationsBell
                    unreadCount={notificationsUnreadCount}
                    setUnreadCount={setNotificationsUnreadCount}
                  />
                <ChatButton
                  onClick={openChat}
                  unreadCount={chatUnreadCount}
                  isOpen={isChatOpen}
                />
                </>
              )}
            </div>
          </div>

          {/* Horizontal Pet Navigation Tabs - shown when viewing a pet */}
          {activeSection === 'pets' && petUuid && !location.pathname.includes('/edit') && !location.pathname.includes('/pets/new') && (
            <nav className="flex items-center gap-1 mt-3 overflow-x-auto -mb-4 pb-0">
              {[
                { label: 'Profile', icon: <Layout className="w-4 h-4" />, path: `/dashboard/pets/${petUuid}`, exact: true },
                {
                  label: 'Medical Summary',
                  icon: <Activity className="w-4 h-4" />,
                  path: latestMedicalRecordId
                    ? `/dashboard/pets/${petUuid}/medical-records/${latestMedicalRecordId}`
                    : `/dashboard/pets/${petUuid}/medical-records/history`
                },
                { label: 'Medications', icon: <Pill className="w-4 h-4" />, path: `/dashboard/pets/${petUuid}/medications` },
                { label: 'Immunizations', icon: <Syringe className="w-4 h-4" />, path: `/dashboard/pets/${petUuid}/immunizations` },
                { label: 'Labs', icon: <FlaskConical className="w-4 h-4" />, path: `/dashboard/pets/${petUuid}/labs` },
                { label: 'Billing', icon: <CreditCard className="w-4 h-4" />, path: `/dashboard/pets/${petUuid}/billing` },
                { label: 'Audit', icon: <History className="w-4 h-4" />, path: `/dashboard/pets/${petUuid}/audit` },
                { label: 'Forms', icon: <FileText className="w-4 h-4" />, path: `/dashboard/pets/${petUuid}/forms` },
              ].map((tab) => {
                const isActive = tab.exact
                  ? location.pathname === tab.path
                  : location.pathname.startsWith(tab.path) || (tab.label === 'Medical Summary' && location.pathname.includes('/medical-records/'));

                return (
                  <button
                    key={tab.label}
                    onClick={() => navigate(tab.path)}
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors border-b-2 cursor-pointer ${
                      isActive
                        ? 'text-blue-600 dark:text-blue-400 border-blue-600 dark:border-blue-400'
                        : 'text-gray-500 dark:text-dark-text-muted border-transparent hover:text-gray-700 dark:hover:text-dark-text-secondary hover:border-gray-300 dark:hover:border-dark-border'
                    }`}
                  >
                    {tab.icon}
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          )}
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-auto bg-white dark:bg-dark-bg-primary">
          {/* Removed global loading - each section handles its own loading state */}

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
              <p className="text-red-800 dark:text-red-300 text-sm">{error}</p>
            </div>
          )}

          {/* Appointments View Toggle */}
          {activeSection === 'appointments' && !loading && (
            <div className="bg-white dark:bg-dark-bg-secondary border-b border-gray-200 dark:border-dark-border px-6 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleViewModeChange('swimlanes')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${appointmentViewMode === 'swimlanes'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-dark-bg-tertiary text-gray-700 dark:text-dark-text-secondary hover:bg-gray-200 dark:hover:bg-dark-bg-hover'
                    }`}
                >
                  Swim lanes
                </button>
                <button
                  onClick={() => handleViewModeChange('calendar')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${appointmentViewMode === 'calendar'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-dark-bg-tertiary text-gray-700 dark:text-dark-text-secondary hover:bg-gray-200 dark:hover:bg-dark-bg-hover'
                    }`}
                >
                  Calendar
                </button>
              </div>
            </div>
          )}

          {/* Appointments Calendar */}
          {activeSection === 'appointments' && !loading && appointmentViewMode === 'calendar' && (
            <AppointmentCalendar
              appointments={appointments}
              currentDate={calendarDate}
              onNavigate={handleCalendarDateChange}
              currentView={calendarView}
              onViewChange={handleCalendarViewChange}
              selectedVetIds={selectedVetIds}
              teamMembers={teamMembers}
              onSelectAppointment={(appointmentId) => {
                const appointment = appointments.find(a => a.id === appointmentId);
                setSelectedAppointmentId(appointmentId);
                setSelectedAppointment(appointment || null);
                setIsAppointmentModalOpen(true);
              }}
              onSelectSlot={(slotInfo) => {
                // Open create appointment modal with pre-filled date/time
                setSelectedSlot(slotInfo);
                setIsCreateAppointmentOpen(true);
              }}
              onRescheduleAppointment={async (appointmentId, start, end) => {
                try {
                  const token = localStorage.getItem('token');
                  if (!token) return;

                  // Find the current appointment to regenerate title
                  const currentAppointment = appointments.find(a => a.id === appointmentId);
                  if (!currentAppointment) return;

                  // Calculate duration in minutes
                  const durationMinutes = Math.round((end.getTime() - start.getTime()) / (1000 * 60));

                  // Regenerate title with new date/time
                  const petNames = currentAppointment.pets?.map((p: any) => p.name).join(', ') || 'Pet';
                  const appointmentTypeLabel = currentAppointment.appointment_type
                    ? currentAppointment.appointment_type.charAt(0).toUpperCase() + currentAppointment.appointment_type.slice(1)
                    : 'Appointment';
                  const dateStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  const timeStr = start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                  const newTitle = `${petNames} ${appointmentTypeLabel} - ${dateStr} at ${timeStr}`;

                  const response = await fetch(`${API_BASE_URL}/api/v1/appointments/${appointmentId}`, {
                    method: 'PUT',
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                      appointment_date: start.toISOString(),
                      duration_minutes: durationMinutes,
                      title: newTitle,
                    }),
                  });

                  if (response.ok) {
                    const updatedAppointment = await response.json();

                    // Update appointments list in place without full refetch
                    setAppointments(prev =>
                      prev.map(appt =>
                        appt.id === appointmentId
                          ? { ...appt, appointment_date: start.toISOString(), duration_minutes: durationMinutes, title: newTitle }
                          : appt
                      )
                    );
                  } else {
                    console.error('Failed to reschedule appointment');
                    alert('Failed to reschedule appointment. Please try again.');
                  }
                } catch (error) {
                  console.error('Error rescheduling appointment:', error);
                  alert('Error rescheduling appointment. Please try again.');
                }
              }}
            />
          )}

          {/* Appointments Swim Lanes/Schedule View */}
          {activeSection === 'appointments' && !loading && appointmentViewMode === 'swimlanes' && (
            <AppointmentSwimLanesView
              appointments={appointments}
              currentDate={calendarDate}
              onNavigate={handleCalendarDateChange}
              teamMembers={teamMembers}
              onSelectAppointment={(appointmentId) => {
                const appointment = appointments.find(a => a.id === appointmentId);
                setSelectedAppointmentId(appointmentId);
                setSelectedAppointment(appointment || null);
                setIsAppointmentModalOpen(true);
              }}
              onSelectSlot={(slotInfo) => {
                // Open create appointment modal with pre-filled date/time
                setSelectedSlot(slotInfo);
                setIsCreateAppointmentOpen(true);
              }}
              onRescheduleAppointment={async (appointmentId, start, end) => {
                try {
                  const token = localStorage.getItem('token');
                  if (!token) return;

                  // Find the current appointment to regenerate title
                  const currentAppointment = appointments.find(a => a.id === appointmentId);
                  if (!currentAppointment) return;

                  // Calculate duration in minutes
                  const durationMinutes = Math.round((end.getTime() - start.getTime()) / (1000 * 60));

                  // Regenerate title with new date/time
                  const petNames = currentAppointment.pets?.map((p: any) => p.name).join(', ') || 'Pet';
                  const appointmentTypeLabel = currentAppointment.appointment_type
                    ? currentAppointment.appointment_type.charAt(0).toUpperCase() + currentAppointment.appointment_type.slice(1)
                    : 'Appointment';
                  const dateStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  const timeStr = start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                  const newTitle = `${petNames} ${appointmentTypeLabel} - ${dateStr} at ${timeStr}`;

                  const response = await fetch(`${API_BASE_URL}/api/v1/appointments/${appointmentId}`, {
                    method: 'PUT',
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                      appointment_date: start.toISOString(),
                      duration_minutes: durationMinutes,
                      title: newTitle,
                    }),
                  });

                  if (response.ok) {
                    // Update appointments list in place
                    setAppointments(prev =>
                      prev.map(appt =>
                        appt.id === appointmentId
                          ? { ...appt, appointment_date: start.toISOString(), duration_minutes: durationMinutes, title: newTitle }
                          : appt
                      )
                    );
                  } else {
                    console.error('Failed to reschedule appointment');
                    alert('Failed to reschedule appointment. Please try again.');
                  }
                } catch (error) {
                  console.error('Error rescheduling appointment:', error);
                  alert('Error rescheduling appointment. Please try again.');
                }
              }}
              onReassignAppointment={async (appointmentId, vetUserId) => {
                try {
                  const token = localStorage.getItem('token');
                  if (!token) return;

                  const response = await fetch(`${API_BASE_URL}/api/v1/appointments/${appointmentId}`, {
                    method: 'PUT',
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                      assigned_vet_user_id: vetUserId,
                    }),
                  });

                  if (response.ok) {
                    // Update appointments list in place
                    setAppointments(prev =>
                      prev.map(appt =>
                        appt.id === appointmentId
                          ? { ...appt, assigned_vet_user_id: vetUserId }
                          : appt
                      )
                    );
                  } else {
                    console.error('Failed to reassign appointment');
                    alert('Failed to reassign appointment. Please try again.');
                  }
                } catch (error) {
                  console.error('Error reassigning appointment:', error);
                  alert('Error reassigning appointment. Please try again.');
                }
              }}
              selectedVetIds={selectedVetIds}
              onToggleAllStaff={(showAll) => {
                if (showAll) {
                  // Show all staff
                  setSelectedVetIds(new Set(teamMembers.map(m => m.id)));
                } else {
                  // Hide all staff
                  setSelectedVetIds(new Set());
                }
              }}
            />
          )}

          {/* Call History */}
          {activeSection === 'calls' && (
            <CallHistorySection />
          )}

          {/* Notifications */}
          {activeSection === 'notifications' && (
            <NotificationsPage
              unreadCount={notificationsUnreadCount}
              onUnreadCountChange={setNotificationsUnreadCount}
            />
          )}

          {/* Broadcast - SMS/Email campaigns to customers */}
          {activeSection === 'broadcast' && (
            <BroadcastPage 
              ref={broadcastRef} 
              showPreviewModal={showCampaignPreviewModal}
              setShowPreviewModal={setShowCampaignPreviewModal}
            />
          )}

          {/* Audience - Customer lists for targeting */}
          {activeSection === 'audience' && (
            <ListsManager ref={audienceManagerRef} onListNameChange={setSelectedAudienceName} />
          )}

          {/* Set And Forget - Automated reminders and form triggers */}
          {activeSection === 'set-and-forget' && (
            <SetAndForgetPage ref={setAndForgetRef} />
          )}

          {/* Templates - Reusable message templates */}
          {activeSection === 'templates' && (() => {
            const path = location.pathname;
            
            // Redirect old ?action=create to /new route
            if (searchParams.get('action') === 'create') {
              const channel = searchParams.get('channel');
              navigate(`/dashboard/templates/new${channel ? `?channel=${channel}` : ''}`, { replace: true });
              return null;
            }
            
            // Redirect old ?templateId= to /:id route  
            const legacyTemplateId = searchParams.get('templateId');
            if (legacyTemplateId) {
              const mode = searchParams.get('mode');
              navigate(`/dashboard/templates/${legacyTemplateId}${mode ? `?mode=${mode}` : ''}`, { replace: true });
              return null;
            }
            
            // /dashboard/templates/new - Create new template
            if (path.endsWith('/templates/new')) {
              return <CreateTemplatePage />;
            }
            
            // /dashboard/templates/:id - Edit existing template
            const templateIdMatch = path.match(/\/templates\/([a-zA-Z0-9-]+)$/);
            if (templateIdMatch && templateIdMatch[1] !== 'new') {
              const templateId = templateIdMatch[1];
              return (
                <TemplateEditView 
                  templateId={templateId} 
                  onBack={() => navigate('/dashboard/templates')} 
                  onSave={() => {}}
                  onTemplateNameChange={setSelectedTemplateName}
                />
              );
            }
            
            // /dashboard/templates - List view
            return <TemplatesManager ref={templatesManagerRef} />;
          })()}

          {/* Audit Log - Notification delivery history */}
          {activeSection === 'audit-log' && (
            <NotificationAuditLog />
          )}

          {/* Client Audit - Pet owner activity history */}
          {activeSection === 'client-audit' && (
            <PetOwnerAuditLog showOwnerColumn={true} />
          )}

          {/* Patient Flow */}
          {activeSection === 'flow' && (
            <PatientFlowView
              appointments={appointments}
              onUpdateStatus={handleUpdateAppointmentStatus}
              onSelectAppointment={(appointmentId) => {
                const appointment = appointments.find(a => a.id === appointmentId);
                setSelectedAppointmentId(appointmentId);
                setSelectedAppointment(appointment || null);
                setIsAppointmentModalOpen(true);
              }}
              teamMembers={teamMembers}
              selectedVetIds={Array.from(selectedVetIds)}
              onVetFilterChange={(vetIds) => setSelectedVetIds(new Set(vetIds))}
              loading={loading}
              onCreateClick={() => { }} // This enables the Create button functionality
              onAppointmentCreated={(newAppointment) => {
                if (newAppointment) {
                  setAppointments(prev => [...prev, newAppointment]);
                }
              }}
            />
          )}

          {/* Work Schedule View Toggle */}
          {activeSection === 'schedule' && !loading && (
            <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setScheduleViewMode('swimlanes');
                    updateCalendarUrl(calendarDate, calendarView, 'swimlanes');
                  }}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${scheduleViewMode === 'swimlanes'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  Swim lanes
                </button>
                <button
                  onClick={() => {
                    setScheduleViewMode('calendar');
                    updateCalendarUrl(calendarDate, calendarView, 'calendar');
                  }}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${scheduleViewMode === 'calendar'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  Calendar
                </button>
              </div>
            </div>
          )}

          {/* Work Schedule Calendar */}
          {activeSection === 'schedule' && !loading && scheduleViewMode === 'calendar' && (
            <WorkScheduleCalendar
              scheduleEvents={schedule}
              currentDate={calendarDate}
              onNavigate={handleCalendarDateChange}
              currentView={calendarView}
              onViewChange={handleCalendarViewChange}
              selectedVetIds={selectedScheduleVetIds}
              teamMembers={teamMembers}
              onRefresh={refreshScheduleData}
              onSelectEvent={(eventId) => {
                const event = schedule.find((s: any) => s.id === eventId);
                if (event) {
                  setSelectedScheduleEvent(event);
                  setIsEditScheduleOpen(true);
                }
              }}
              onSelectSlot={(slotInfo) => {
                setSelectedScheduleSlot(slotInfo);
                setIsCreateScheduleOpen(true);
              }}
              onRescheduleEvent={async (eventId, start, end) => {
                try {
                  const response = await fetch(
                    `${API_BASE_URL}/api/v1/scheduling-unix/vet-availability/${eventId}`,
                    {
                      method: 'PUT',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                      },
                      body: JSON.stringify({
                        start_at: start.toISOString(),
                        end_at: end.toISOString(),
                      }),
                    }
                  );
                  if (response.ok) {
                    await refreshScheduleData(); // Invalidate cache and refresh
                  }
                } catch (err) {
                  console.error('Error rescheduling availability:', err);
                }
              }}
            />
          )}

          {/* Work Schedule Swim Lanes View */}
          {activeSection === 'schedule' && !loading && scheduleViewMode === 'swimlanes' && (
            <WorkScheduleSwimLanesView
              scheduleEvents={schedule}
              currentDate={calendarDate}
              onNavigate={handleCalendarDateChange}
              selectedVetIds={selectedScheduleVetIds}
              teamMembers={teamMembers}
              onRefresh={refreshScheduleData}
              onSelectEvent={(eventId) => {
                const event = schedule.find((s: any) => s.id === eventId);
                if (event) {
                  setSelectedScheduleEvent(event);
                  setIsEditScheduleOpen(true);
                }
              }}
              onSelectSlot={(slotInfo) => {
                setSelectedScheduleSlot(slotInfo);
                setIsCreateScheduleOpen(true);
              }}
              onRescheduleEvent={async (eventId, start, end) => {
                try {
                  const response = await fetch(
                    `${API_BASE_URL}/api/v1/scheduling-unix/vet-availability/${eventId}`,
                    {
                      method: 'PUT',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                      },
                      body: JSON.stringify({
                        start_at: start.toISOString(),
                        end_at: end.toISOString(),
                      }),
                    }
                  );
                  if (response.ok) {
                    await refreshScheduleData(); // Invalidate cache and refresh
                  }
                } catch (err) {
                  console.error('Error rescheduling availability:', err);
                }
              }}
            />
          )}

          {/* Pets / Owners View */}
          {activeSection === 'pets' && (
            <>
              {location.pathname.includes('/pets/new') ? (
                <PetForm mode="create" />
              ) : reminderId && reminderPetId ? (
                <ReminderEditPage petId={reminderPetId} reminderId={reminderId} />
              ) : location.pathname.includes('/edit') && !location.pathname.includes('/medical-records/') && !location.pathname.includes('/reminders/') && petUuid ? (
                <PetForm mode="edit" petId={petUuid} />
              ) : location.pathname.includes('/immunizations') && petUuid ? (
                <div className="space-y-6">
                  <ImmunizationsList
                    petId={petUuid}
                    refreshTrigger={immunizationRefreshTrigger}
                    onEdit={(imm) => {
                      setSelectedImmunization(imm);
                      setIsImmunizationModalOpen(true);
                    }}
                  />

                  {/* Schedule View */}
                  <div className="border-t-2 border-gray-200 pt-6">
                    <ImmunizationScheduleView
                      petId={petUuid}
                      onRefresh={() => setImmunizationRefreshTrigger(prev => prev + 1)}
                    />
                  </div>
                </div>
              ) : location.pathname.includes('/labs') && petUuid ? (
                <PetLabs petId={petUuid} />
              ) : location.pathname.includes('/medications') && petUuid ? (
                <PetMedications petId={petUuid} />
              ) : location.pathname.includes('/billing') && petUuid ? (
                <PetBilling petId={petUuid} />
              ) : location.pathname.includes('/audit') && petUuid ? (
                <PetAuditLog petId={petUuid} />
              ) : location.pathname.includes('/forms') && petUuid ? (
                <PetForms petId={petUuid} petOwnerId={selectedOwnerId || undefined} />
              ) : location.pathname.includes('/medical-records/history') && petUuid ? (
                <MedicalRecordHistory petId={petUuid} />
              ) : location.pathname.includes('/medical-records/create') && petUuid ? (
                <MedicalRecordForm mode="create" petId={petUuid} />
              ) : location.pathname.includes('/edit') && location.pathname.includes('/medical-records/') && petUuid ? (
                <MedicalRecordForm mode="edit" petId={petUuid} recordId={recordId} />
              ) : location.pathname.includes('/medical-records/') && petUuid ? (
                <MedicalRecordDetail petId={petUuid} recordId={recordId} />
              ) : petUuid ? (
                <PetDetail petId={petUuid} />
              ) : selectedOwnerId && location.pathname.includes('/edit') ? (
                <PetOwnerForm mode="edit" ownerUuid={selectedOwnerId} inDashboard={true} />
              ) : selectedOwnerId ? (
                <PetOwnerDetail 
                  ownerIdProp={selectedOwnerId} 
                  inDashboard={true} 
                  onBookAppointment={handleBookAppointmentFromPet}
                />
              ) : (
                <>
                  <div className="bg-white border-t border-gray-200 overflow-auto flex-1">
                    {loading ? (
                      <div className="animate-pulse">
                        <table className="min-w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pets</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Visited</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Next Appt</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Touches</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reminders</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {[1, 2, 3, 4, 5].map((i) => (
                              <tr key={i}>
                                <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-32"></div></td>
                                <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-40"></div></td>
                                <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-28"></div></td>
                                <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-12"></div></td>
                                <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-24"></div></td>
                                <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-24"></div></td>
                                <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-12"></div></td>
                                <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-12"></div></td>
                                <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-20"></div></td>
                                <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-20 ml-auto"></div></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <>
                      {/* Stats Cards - like Communication Audit page */}
                      <div className="p-6 border-b border-gray-200 bg-white">
                        {clientsStats && (
                          <div className="grid grid-cols-6 gap-4 mb-4">
                            <button
                              onClick={() => { setStatusFilter(null); }}
                              className={`text-left rounded-lg p-4 transition-all ${
                                statusFilter === null && !appointmentsTodayFilter && !needsAppointmentFilter
                                  ? 'bg-gray-100 ring-2 ring-gray-400'
                                  : 'bg-gray-50 hover:bg-gray-100'
                              }`}
                            >
                              <div className="text-2xl font-bold text-gray-900">{clientsStats.total}</div>
                              <div className="text-sm text-gray-500">All Clients</div>
                            </button>
                            <button
                              onClick={() => { setAppointmentsTodayFilter(true); }}
                              className={`text-left rounded-lg p-4 transition-all ${
                                appointmentsTodayFilter
                                  ? 'bg-teal-100 ring-2 ring-teal-500'
                                  : 'bg-teal-50 hover:bg-teal-100'
                              }`}
                            >
                              <div className="text-2xl font-bold text-teal-600">{clientsStats.appointments_today}</div>
                              <div className="text-sm text-gray-500">Today's Appts</div>
                            </button>
                            <button
                              onClick={() => { setStatusFilter('OPEN'); }}
                              className={`text-left rounded-lg p-4 transition-all ${
                                statusFilter === 'OPEN' && !appointmentsTodayFilter && !needsAppointmentFilter
                                  ? 'bg-green-100 ring-2 ring-green-500'
                                  : 'bg-green-50 hover:bg-green-100'
                              }`}
                            >
                              <div className="text-2xl font-bold text-green-600">{clientsStats.open}</div>
                              <div className="text-sm text-gray-500">Active Clients</div>
                            </button>
                            <button
                              onClick={() => { setStatusFilter('COLD'); }}
                              className={`text-left rounded-lg p-4 transition-all ${
                                statusFilter === 'COLD' && !appointmentsTodayFilter
                                  ? 'bg-amber-100 ring-2 ring-amber-500'
                                  : 'bg-amber-50 hover:bg-amber-100'
                              }`}
                            >
                              <div className="text-2xl font-bold text-amber-600">{clientsStats.cold}</div>
                              <div className="text-sm text-gray-500">Needs Appt</div>
                            </button>
                            <button
                              onClick={() => { setStatusFilter('PROSPECT'); }}
                              className={`text-left rounded-lg p-4 transition-all ${
                                statusFilter === 'PROSPECT' && !appointmentsTodayFilter && !needsAppointmentFilter
                                  ? 'bg-blue-100 ring-2 ring-blue-500'
                                  : 'bg-blue-50 hover:bg-blue-100'
                              }`}
                            >
                              <div className="text-2xl font-bold text-blue-600">{clientsStats.prospect}</div>
                              <div className="text-sm text-gray-500">Prospect</div>
                            </button>
                            <button
                              onClick={() => { setStatusFilter('LOST'); }}
                              className={`text-left rounded-lg p-4 transition-all ${
                                statusFilter === 'LOST' && !appointmentsTodayFilter && !needsAppointmentFilter
                                  ? 'bg-red-100 ring-2 ring-red-500'
                                  : 'bg-red-50 hover:bg-red-100'
                              }`}
                            >
                              <div className="text-2xl font-bold text-red-600">{clientsStats.lost}</div>
                              <div className="text-sm text-gray-500">Lost</div>
                            </button>
                          </div>
                        )}
                      </div>
                      
                      {owners.length > 0 && (
                      <div>
                      <div className="overflow-x-auto">
                      <table className="min-w-full table-fixed">
                        <thead className="bg-gray-50 sticky top-0 z-10">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-40">Name</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-48">Email</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-32">Phone</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24">Pets</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24">Visited</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24">Next</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase w-16">Touches</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-20">Remind</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-28">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {/* Server-side pagination - owners already contains current page only */}
                          {owners.map((owner: any) => {
                            const crmData = ownersCrmData[owner.uuid];
                            const formatDate = (dateStr: string | null) => {
                              if (!dateStr) return '-';
                              try {
                                return new Date(dateStr).toLocaleDateString();
                              } catch {
                                return '-';
                              }
                            };

                            return (
                              <tr
                                key={owner.uuid}
                                className="hover:bg-gray-50 transition-colors"
                              >
                                <td className="px-3 py-2 whitespace-nowrap">
                                  <button
                                    onClick={() => navigate(`/dashboard/pet_owners/${owner.uuid}`)}
                                    className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline truncate max-w-[150px] block"
                                    title={owner.full_name}
                                  >
                                    {owner.full_name}
                                  </button>
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600 truncate max-w-[180px]" title={owner.email}>
                                  {owner.email || '-'}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">
                                  {owner.phone ? formatPhoneNumber(owner.phone) : '-'}
                                </td>
                                <td className="px-3 py-2">
                                  {owner.pets && owner.pets.length > 0 ? (
                                    <PetsDropdown
                                      pets={owner.pets}
                                      onPetClick={(pet: any) => {
                                        const petId = pet.id || pet.uuid || pet.pet_id;
                                        navigate(`/dashboard/pets/${petId}`);
                                      }}
                                    />
                                  ) : (
                                    <span className="text-xs text-gray-400">-</span>
                                  )}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">
                                  {crmData ? formatDate(crmData.last_visit_date) : '-'}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">
                                  {crmData?.next_appointment_date ? formatDate(crmData.next_appointment_date) : '-'}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-center">
                                  <span className="text-sm text-gray-700">
                                    {crmData?.touch_count ?? 0}
                                  </span>
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  {crmData && crmData.configured_reminders && crmData.configured_reminders.length > 0 ? (
                                    <RemindersDropdown
                                      reminders={crmData.configured_reminders}
                                    />
                                  ) : (
                                    <span className="text-xs text-gray-400">-</span>
                                  )}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  {crmData ? (
                                    <CustomerStatusDropdown
                                      status={crmData.customer_status as CustomerStatus}
                                      isOverride={crmData.customer_status_override}
                                      petOwnerId={owner.uuid}
                                      petOwnerName={owner.full_name}
                                      onStatusUpdate={async () => {
                                        await fetchData();
                                      }}
                                      canEdit={user?.role === 'ADMIN' || user?.role === 'PRACTICE_ADMIN' || user?.role === 'VET_STAFF'}
                                    />
                                  ) : (
                                    <span className="text-xs text-gray-400">-</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      </div>
                      
                      {/* Pagination Controls */}
                      {(() => {
                        // Server-side pagination - owners already contains just current page
                        const startItem = (clientsPage - 1) * clientsPageSize + 1;
                        const endItem = Math.min(clientsPage * clientsPageSize, clientsTotal);
                        
                        if (clientsTotalPages <= 1) return null;
                        
                        return (
                          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                            <div className="text-sm text-gray-600">
                              Showing {startItem}-{endItem} of {clientsTotal} clients
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => setClientsPage(p => Math.max(1, p - 1))}
                                disabled={clientsPage === 1}
                                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Previous
                              </button>
                              <span className="text-sm text-gray-600">
                                Page {clientsPage} of {clientsTotalPages}
                              </span>
                              <button
                                onClick={() => setClientsPage(p => Math.min(clientsTotalPages, p + 1))}
                                disabled={clientsPage === clientsTotalPages}
                                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        );
                      })()}
                      </div>
                    )}
                    
                    {owners.length === 0 && (
                      <div className="p-8 text-center text-gray-500">
                        No clients match your search
                      </div>
                    )}
                    </>
                    )}
                  </div>
                </>
              )}
            </>
          )}

          {/* Admin Billing Dashboard (ADMIN only) */}
          {activeSection === 'admin-billing' && isAdmin && (
            <BillingAdminDashboard />
          )}

        </div>
      </div>


      {/* Pet Owner Detail Modal */}
      {selectedOwnerId && (
        <PetOwnerDetailModal
          isOpen={isOwnerModalOpen}
          onClose={() => {
            setIsOwnerModalOpen(false);
            setSelectedOwnerId(null);
            navigate('/dashboard/pet_owners');
          }}
          ownerId={selectedOwnerId}
        />
      )}

      {/* Create Pet Owner Sliding Panel */}
      <PetOwnerCreateModal
        isOpen={isCreateOwnerOpen}
        onClose={() => navigate('/dashboard/pet_owners')}
        onSuccess={() => {
          fetchData(); // Refresh the list
          navigate('/dashboard/pet_owners');
        }}
      />

      {/* Edit Pet Owner Modal */}
      {selectedOwnerId && (
        <PetOwnerEditModal
          isOpen={isOwnerEditModalOpen}
          onClose={() => {
            setIsOwnerEditModalOpen(false);
            navigate(`/dashboard/pet_owners/${selectedOwnerId}`);
          }}
          onSuccess={() => {
            setIsOwnerEditModalOpen(false);
            fetchData(); // Refresh the data
            navigate(`/dashboard/pet_owners/${selectedOwnerId}`);
          }}
          ownerUuid={selectedOwnerId}
        />
      )}

      {/* Create Appointment Modal */}
      <AppointmentCreateModal
        isOpen={isCreateAppointmentOpen}
        onClose={() => {
          setIsCreateAppointmentOpen(false);
          setSelectedSlot(null);
          setAppointmentPetId(null);
          setAppointmentOwnerId(null);
        }}
        onSuccess={(newAppointment) => {
          setIsCreateAppointmentOpen(false);
          setSelectedSlot(null);
          setAppointmentPetId(null);
          setAppointmentOwnerId(null);
          // Optimistically add the new appointment to state instead of full refresh
          if (newAppointment) {
            setAppointments(prev => [...prev, newAppointment]);
          }
        }}
        initialDate={selectedSlot?.start}
        initialEndTime={selectedSlot?.end}
        initialVetUserId={selectedSlot?.vetUserId}
        initialPetOwnerId={appointmentOwnerId || undefined}
        initialPetId={appointmentPetId || undefined}
      />

      {/* CSV Import Modal */}
      <CsvImportModal
        isOpen={isCsvImportOpen}
        onClose={() => setIsCsvImportOpen(false)}
        onSuccess={() => {
          // Refresh the owners list after successful import
          const fetchOwners = async () => {
            try {
              const token = localStorage.getItem('token');
              const response = await fetch(API_ENDPOINTS.PET_OWNERS.LIST, {
                headers: { 'Authorization': `Bearer ${token}` },
              });
              if (response.ok) {
                const data = await response.json();
                setOwners(data);
              }
            } catch (error) {
              console.error('Failed to refresh owners:', error);
            }
          };
          fetchOwners();
        }}
      />

      {/* Create Work Schedule Modal */}
      <WorkScheduleCreateModal
        isOpen={isCreateScheduleOpen}
        onClose={() => {
          setIsCreateScheduleOpen(false);
          setSelectedScheduleSlot(null);
        }}
        onSuccess={() => {
          setIsCreateScheduleOpen(false);
          setSelectedScheduleSlot(null);
          refreshScheduleData(); // Invalidate cache and refresh
        }}
        initialStart={selectedScheduleSlot?.start}
        initialEnd={selectedScheduleSlot?.end}
        initialVetId={selectedScheduleSlot?.vetId}
      />

      {/* Edit Work Schedule Modal */}
      <WorkScheduleEditModal
        isOpen={isEditScheduleOpen}
        onClose={() => {
          setIsEditScheduleOpen(false);
          setSelectedScheduleEvent(null);
        }}
        onSuccess={() => {
          setIsEditScheduleOpen(false);
          setSelectedScheduleEvent(null);
          refreshScheduleData(); // Invalidate cache and refresh
        }}
        event={selectedScheduleEvent}
      />



      {/* Appointment View Modal - Google Calendar Style */}
      <AppointmentViewModal
        isOpen={isAppointmentModalOpen}
        onClose={() => {
          setIsAppointmentModalOpen(false);
          setSelectedAppointmentId(null);
          setSelectedAppointment(null);
        }}
        appointment={selectedAppointment}
        onEdit={() => {
          // RESTful: Navigate to URL with edit param
          if (selectedAppointment?.id) {
            setIsAppointmentModalOpen(false);
            const newParams = new URLSearchParams(searchParams);
            newParams.set('edit', selectedAppointment.id);
            setSearchParams(newParams, { replace: true });
          }
        }}
        onDelete={() => {
          setIsAppointmentModalOpen(false);
          setShowDeleteAppointmentConfirm(true);
        }}
      />

      <AppointmentEditModal
        isOpen={!!editAppointmentId}
        onClose={closeEditModal}
        appointmentId={editAppointmentId || ''}
        onSuccess={(updatedAppointment) => {
          // Optimistically update the appointment in state
          if (updatedAppointment) {
            setAppointments(prev => prev.map(appt =>
              appt.id === updatedAppointment.id ? updatedAppointment : appt
            ));
          }
          closeEditModal();
          setIsAppointmentModalOpen(false);
        }}
      />

      {/* Immunization Modal */}
      {petUuid && (
        <ImmunizationFormModal
          isOpen={isImmunizationModalOpen}
          onClose={() => setIsImmunizationModalOpen(false)}
          onSuccess={() => {
            setImmunizationRefreshTrigger(prev => prev + 1);
            setIsImmunizationModalOpen(false);
          }}
          petId={petUuid}
          immunizationToEdit={selectedImmunization}
        />
      )}

      {/* Delete Appointment Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteAppointmentConfirm}
        onClose={() => setShowDeleteAppointmentConfirm(false)}
        onConfirm={async () => {
          if (!selectedAppointmentId) return;

          try {
            const token = localStorage.getItem('access_token') || localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/v1/appointments/${selectedAppointmentId}`, {
              method: 'DELETE',
              headers: {
                'Authorization': `Bearer ${token}`,
              },
            });

            if (response.ok) {
              // Optimistically update local state - mark as cancelled instead of full refresh
              setAppointments(prev => 
                prev.map(appt => 
                  appt.id === selectedAppointmentId 
                    ? { ...appt, status: 'cancelled' }
                    : appt
                )
              );
              setShowDeleteAppointmentConfirm(false);
              setIsAppointmentModalOpen(false);
              setSelectedAppointmentId(null);
              setSelectedAppointment(null);
            } else {
              alert('Failed to delete appointment. Please try again.');
            }
          } catch (error) {
            console.error('Error deleting appointment:', error);
            alert('Error deleting appointment. Please try again.');
          }
        }}
        title="Delete Appointment"
        message="Are you sure you want to delete this appointment?"
        confirmText="Delete"
        cancelText="Cancel"
        isDangerous={true}
      />

      {/* Invite Team Member Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Invite Team Member</h3>
              <button
                onClick={() => {
                  setShowInviteModal(false);
                  setInviteEmail('');
                  setInviteError(null);
                  setInviteSuccess(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {inviteSuccess && (
              <div className="mb-4 bg-green-50 border border-green-200 rounded-md p-3">
                <p className="text-sm text-green-800">{inviteSuccess}</p>
              </div>
            )}

            {inviteError && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-800">{inviteError}</p>
              </div>
            )}

            <form onSubmit={handleSendInvite}>
              <div className="mb-4">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="colleague@example.com"
                  required
                  disabled={isInviting}
                />
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowInviteModal(false);
                    setInviteEmail('');
                    setInviteError(null);
                    setInviteSuccess(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  disabled={isInviting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isInviting}
                >
                  {isInviting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <UserPlus className="mr-2 h-4 w-4" />
                      Send Invitation
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Revoke Invite Confirmation */}
      <ConfirmDialog
        isOpen={revokeInviteId !== null}
        onClose={() => setRevokeInviteId(null)}
        onConfirm={handleRevokeInvite}
        title="Revoke Invitation"
        message="Are you sure you want to revoke this invitation? This action cannot be undone."
        confirmText="Revoke"
        isDangerous={true}
      />

      {/* Remove Team Member Confirmation */}
      <ConfirmDialog
        isOpen={removeMemberId !== null}
        onClose={() => setRemoveMemberId(null)}
        onConfirm={handleRemoveMember}
        title="Remove Team Member"
        message="Are you sure you want to remove this team member from your practice? They will lose access immediately."
        confirmText="Remove"
        isDangerous={true}
      />

      {/* Help Modal */}
      {showHelpModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/20" onClick={() => setShowHelpModal(false)} />
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative z-10">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <HelpCircle className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Need Help?</h3>
              </div>
              <button
                onClick={() => setShowHelpModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-6">
              Our support team is here to help you with any questions or issues you may have.
            </p>

            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                <Phone className="w-5 h-5 text-gray-600 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-1">Phone</div>
                  <a
                    href="tel:+14157549214"
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    (415) 754-9214
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                <Mail className="w-5 h-5 text-gray-600 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-1">Email</div>
                  <a
                    href="mailto:hi@helppet.ai"
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    hi@helppet.ai
                  </a>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={() => setShowHelpModal(false)}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pet Detail Modal (View) */}


      {/* Pet Edit Modal */}
      {selectedPetId && isPetEditModalOpen && (
        <PetEditModal
          isOpen={isPetEditModalOpen}
          onClose={() => {
            setIsPetEditModalOpen(false);
            setSelectedPetId(null);
          }}
          petId={selectedPetId}
          initialMode="edit"
          onSuccess={() => {
            setIsPetEditModalOpen(false);
            setSelectedPetId(null);
            fetchData(); // Refresh the data
          }}
        />
      )}

    </div>
  );
};

export default Dashboard;
